// ============================================================================
// src/components/block/liveStreamManagment/ProductionControlRoom_MediaboardConfig.tsx
// v4.3 : dimensions natives pour les incrustations de type source (caméra)
// ============================================================================

import React, {
  useState, useRef, useEffect, useCallback,
} from 'react';
import {
  Settings, Monitor, Layout, Plus, Trash2, Save, FolderOpen,
  Image as ImageIcon, Film, Music, Info,
} from 'lucide-react';
import {
  ImageSlot, SoundSlot, VideoOverlaySlot, JingleSlot,
  ScreenShareConfig, DEFAULT_SCREEN_SHARE_CONFIG, ScreenShareSlot,
  LayoutSlot, LayoutBackground, LayoutBackgroundType, LayoutBackgroundFit, LayoutIncrustation,
  VideoSource,
  DEFAULT_IMAGE_SLOTS, DEFAULT_SOUND_SLOTS, DEFAULT_VIDEO_OVERLAY_SLOTS, DEFAULT_JINGLE_SLOTS,
  DEFAULT_SCREEN_SHARE_SLOTS, DEFAULT_LAYOUT_SLOTS,
} from '../../../contexts/MediaStreamContext';
import {
  storeMediaSource, deleteMediaRecord, clearSlotsFromIndexedDB, clearSceneSlotsFromIndexedDB,
} from '../../../backendServices/liveStreamManagment/local_media_store';
import { useSourceRegistry } from '../../../contexts/liveStreamManagment/SourceRegistryContext';
import { RegieConfigModal, RegieConfigPayload } from './RegieConfigModal';
import { retargetPipDevices } from '../../../dataTypes/liveStreamManagment/deck_persistence';
import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState } from '../../../store/generalReducer';

import SoundWaveform from './SoundWaveform'
import { DeviceStreamPreview } from './ProductionControlRoom_SharedUI'
import { computeBgTileRects } from './useCanvasPipeline'
import Window from '../../widget/windows/window';
import { LibraryPicker, LibraryKind, libraryKindFromAccept } from './ProductionControlRoom_LibraryPicker';
import { FieldMedia, MediaType } from '../../widget/inputs/FieldMedia';
import { MediaboardVideo } from './MediaboardVideo';
import { isHlsUrl, posterOfHlsUrl, setVideoSource, releaseVideoSource } from '../../../dataTypes/liveStreamManagment/utils_videoSource';

// ─── Résolution de référence ──────────────────────────────────────────────────

const REF_W = 1920;
const REF_H = 1080;

// ─── Palettes couleur ─────────────────────────────────────────────────────────

const PAL = {
  image:  { stroke: '#22d3ee', fill: 'rgba(34,211,238,0.25)',  text: '#a5f3fc', badge: '#22d3ee' },
  sound:  { stroke: '#facc15', fill: 'rgba(250,204,21,0.15)',  text: '#fef08a', badge: '#facc15' },
  pip:    { stroke: '#e879f9', fill: 'rgba(232,121,249,0.25)', text: '#f0abfc', badge: '#e879f9' },
  jingle: { stroke: '#f87171', fill: 'rgba(248,113,113,0.15)', text: '#fca5a5', badge: '#f87171' },
  screen: { stroke: '#34d399', fill: 'rgba(52,211,153,0.15)',  text: '#6ee7b7', badge: '#34d399' },
  layout: { stroke: '#60a5fa', fill: 'rgba(96,165,250,0.15)',  text: '#93c5fd', badge: '#60a5fa' },
};

// ─── Échelle des textes (2026-10-23) ──────────────────────────────────────────
// Remplace les blancs à 18–75 % d'opacité, illisibles sur le fond sombre : chaque
// niveau garde au moins ≈ 5:1 de contraste (WCAG AA) sur --color-Panel-niv1-BG.

const TXT = {
  body:  '#e2e8f0',   // texte courant, valeurs
  label: '#cbd5e1',   // étiquettes (« Nom : », « Vol. »)
  help:  '#94a3b8',   // aides, unités, titres de section
  faint: '#7c8aa0',   // vide, « Aucun aperçu », indications secondaires
};

// ─── Styles partagés ──────────────────────────────────────────────────────────

const inputCss: React.CSSProperties = {
  width: '100%', background: '#111a2e', color: '#ffffff',
  border: '1px solid rgba(255,255,255,0.16)', borderRadius: 6,
  padding: '4px 8px', fontSize: 12, outline: 'none',
  boxSizing: 'border-box',
};

// Hauteur fixe pour tous les inputs/boutons sur la même ligne
const INPUT_H = 28;

const dimLabelCss: React.CSSProperties = {
  fontSize: 11, color: TXT.label,
  display: 'flex', flexDirection: 'column', gap: 2,
};

const sectionBoxCss: React.CSSProperties = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12, padding: 12,
  display: 'flex', flexDirection: 'column', gap: 8,
};

const sectionLabelCss: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, color: TXT.help,
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2,
};

// Boutons de l'en-tête et du pied de la fenêtre (2026-10-23).
const HEAD_BTN: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 7, height: 34,
  padding: '0 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
  transition: 'all 0.15s',
};

/** Aide sous un aperçu : icône, texte lisible, sur une seule carte discrète. */
const HintBar: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: 8,
    padding: '8px 12px', borderRadius: 10,
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
    fontSize: 12, color: TXT.help, lineHeight: 1.6,
  }}>
    <Info size={14} style={{ flexShrink: 0, marginTop: 2, color: TXT.help }} />
    <div>{children}</div>
  </div>
);

// Largeur uniforme pour tous les champs numériques
const FIELD_W = 56;

// ─── Conversion % ↔ px ───────────────────────────────────────────────────────

const pctToPx = (pct: number, dim: number) => Math.round(pct / 100 * dim);
const pxToPct = (px: number, dim: number) => px / dim * 100;

// Options de mode d'adaptation du fond de scène — voir computeBgTileRects (useCanvasPipeline)
// pour la logique de calcul, partagée avec le pipeline de compositing afin que l'aperçu
// WYSIWYG corresponde exactement au rendu diffusé.
const BG_FIT_OPTIONS: { value: LayoutBackgroundFit; label: string; title: string }[] = [
  { value: 'native',    label: 'Taille native',    title: "Afficher le média à sa taille réelle, centré, sans mise à l'échelle" },
  { value: 'stretchXY', label: 'Adapter X/Y',      title: "Étirer/contracter le média sur X et Y pour remplir exactement le cadre (déforme l'image)" },
  { value: 'stretchX',  label: 'Adapter X',        title: "Étirer/contracter le média sur X pour correspondre à la largeur du cadre (hauteur native)" },
  { value: 'stretchY',  label: 'Adapter Y',        title: "Étirer/contracter le média sur Y pour correspondre à la hauteur du cadre (largeur native)" },
  { value: 'repeatX',   label: 'Dupliquer X',      title: 'Dupliquer le média en mosaïque horizontalement, à taille native' },
  { value: 'repeatY',   label: 'Dupliquer Y',      title: 'Dupliquer le média en mosaïque verticalement, à taille native' },
  { value: 'repeatXY',  label: 'Dupliquer X/Y',    title: 'Dupliquer le média en mosaïque sur X et Y, à taille native' },
];

// Icônes des modes d'adaptation du fond — un pictogramme par valeur de BG_FIT_OPTIONS,
// même style (trait 1.3px, viewBox 14×14) que les IconActionButton de PositionSizeGrid.
const BG_FIT_ICONS: Record<LayoutBackgroundFit, React.ReactNode> = {
  native: (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="1.5" width="11" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
      <rect x="4" y="4" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.1"/>
    </svg>
  ),
  stretchXY: (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M2 5V2h3M9 2h3v3M12 9v3H9M5 12H2V9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="4.5" y="4.5" width="5" height="5" rx="0.8" stroke="currentColor" strokeWidth="1.1"/>
    </svg>
  ),
  stretchX: (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <rect x="4" y="4" width="6" height="6" rx="0.8" stroke="currentColor" strokeWidth="1.1"/>
      <path d="M2 7H4M10 7H12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M3.2 5.3L1.8 7L3.2 8.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10.8 5.3L12.2 7L10.8 8.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  stretchY: (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <rect x="4" y="4" width="6" height="6" rx="0.8" stroke="currentColor" strokeWidth="1.1"/>
      <path d="M7 2V4M7 10V12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M5.3 3.2L7 1.8L8.7 3.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5.3 10.8L7 12.2L8.7 10.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  repeatX: (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <rect x="1.2" y="4" width="4.6" height="6" rx="0.8" stroke="currentColor" strokeWidth="1.1"/>
      <rect x="8.2" y="4" width="4.6" height="6" rx="0.8" stroke="currentColor" strokeWidth="1.1"/>
    </svg>
  ),
  repeatY: (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <rect x="4" y="1.2" width="6" height="4.6" rx="0.8" stroke="currentColor" strokeWidth="1.1"/>
      <rect x="4" y="8.2" width="6" height="4.6" rx="0.8" stroke="currentColor" strokeWidth="1.1"/>
    </svg>
  ),
  repeatXY: (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <rect x="1.2" y="1.2" width="4.6" height="4.6" rx="0.6" stroke="currentColor" strokeWidth="1.1"/>
      <rect x="8.2" y="1.2" width="4.6" height="4.6" rx="0.6" stroke="currentColor" strokeWidth="1.1"/>
      <rect x="1.2" y="8.2" width="4.6" height="4.6" rx="0.6" stroke="currentColor" strokeWidth="1.1"/>
      <rect x="8.2" y="8.2" width="4.6" height="4.6" rx="0.6" stroke="currentColor" strokeWidth="1.1"/>
    </svg>
  ),
};

// ─── Persistance noms de fichiers ─────────────────────────────────────────────

const FILENAMES_KEY = 'mediartc_slot_filenames';
function loadFileNames(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(FILENAMES_KEY) || '{}'); } catch { return {}; }
}
function saveFileNames(names: Record<string, string>): void {
  try { localStorage.setItem(FILENAMES_KEY, JSON.stringify(names)); } catch {}
}
function stripExt(filename: string): string {
  return filename.replace(/\.[^/.]+$/, '').trim();
}

// Formate un timecode (secondes) en mm:ss.d
function fmtTimecode(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const d = Math.floor((sec % 1) * 10);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${d}`;
}

// ══════════════════════════════════════════════════════════════════════════════
// FilePickerButton — FieldMedia de l'application, sans aperçu : nom du fichier,
// puis disque local, médias du compte (Library) et poubelle
// ══════════════════════════════════════════════════════════════════════════════

const FORCE_TYPE_BY_KIND: Record<LibraryKind, MediaType> = { image: 'Image', sound: 'Sound', video: 'Video' };

interface FilePickerButtonProps {
  accept: string; filename: string;
  onFile: (dataUrl: string, rawName: string) => void;
  /** Absent : pas de poubelle (rien à réinitialiser). */
  onDelete?: () => void;
  label?: string;
}

const FilePickerButton: React.FC<FilePickerButtonProps> = ({
  accept, filename, onFile, onDelete, label,
}) => {
  const libraryKind = libraryKindFromAccept(accept);
  const [showLibrary, setShowLibrary] = useState(false);

  // FieldMedia remonte aussi la saisie du champ texte (une chaîne) : seul un
  // fichier choisi sur le disque nous intéresse.
  const handleChange = (_e: React.ChangeEvent<HTMLInputElement>, value: string | number | File) => {
    if (!(value instanceof File)) return;
    const reader = new FileReader();
    reader.onload = ev => { if (ev.target?.result) onFile(ev.target.result as string, value.name); };
    reader.readAsDataURL(value);
  };

  return (
    <>
      <FieldMedia
        value={filename}
        placeholder={label || 'Choisir un fichier…'}
        forceType={libraryKind ? FORCE_TYPE_BY_KIND[libraryKind] : undefined}
        showPreview={false}
        library={!!libraryKind}
        onShowLibrary={() => setShowLibrary(true)}
        onChange={handleChange}
        onDelete={onDelete}
      />
      {showLibrary && libraryKind && (
        <LibraryPicker kind={libraryKind} onClose={() => setShowLibrary(false)} onFile={onFile} />
      )}
    </>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// PxInput — largeur uniforme FIELD_W
// ══════════════════════════════════════════════════════════════════════════════

interface PxInputProps {
  label: string; valuePct: number; refDim: number;
  min?: number; max?: number;
  onChange: (pct: number) => void;
}

const PxInput: React.FC<PxInputProps> = ({
  label, valuePct, refDim, min = 0, max, onChange,
}) => {
  const extPx = pctToPx(valuePct, refDim);
  const [local, setLocal] = useState(String(extPx));
  const prev = useRef(extPx);
  if (prev.current !== extPx) { prev.current = extPx; setLocal(String(extPx)); }
  const commit = () => {
    const px = parseInt(local, 10);
    if (isNaN(px)) { setLocal(String(pctToPx(valuePct, refDim))); return; }
    const c = Math.max(min, max !== undefined ? Math.min(max, px) : px);
    setLocal(String(c)); onChange(pxToPct(c, refDim));
  };
  const liveChange = (raw: string) => {
    setLocal(raw);
    const px = parseInt(raw, 10);
    if (isNaN(px)) return;
    const c = Math.max(min, max !== undefined ? Math.min(max, px) : px);
    onChange(pxToPct(c, refDim));
  };
  return (
    <label style={{ ...dimLabelCss }}>
      <span style={{ color: TXT.label, fontSize: 12 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <input
          type="number" value={local} min={min} max={max}
          onChange={e => liveChange(e.target.value)}
          onBlur={commit} onKeyDown={e => e.key === 'Enter' && commit()}
          style={{ ...inputCss, width: FIELD_W, height: INPUT_H, padding: '0 4px', fontSize: 12, fontFamily: 'monospace', boxSizing: 'border-box' }}
        />
        <span style={{ fontSize: 12, color: TXT.help, flexShrink: 0 }}>px</span>
      </div>
    </label>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// PctInput — largeur uniforme FIELD_W
// ══════════════════════════════════════════════════════════════════════════════

interface PctInputProps {
  label: string; value: number;
  onChange: (v: number) => void;
}

const PctInput: React.FC<PctInputProps> = ({ label, value, onChange }) => {
  const [local, setLocal] = useState(String(Math.round(value * 100)));
  const prev = useRef(value);
  if (prev.current !== value) { prev.current = value; setLocal(String(Math.round(value * 100))); }
  const commit = () => {
    const pct = parseInt(local, 10);
    if (isNaN(pct)) { setLocal(String(Math.round(value * 100))); return; }
    const c = Math.max(0, Math.min(100, pct));
    setLocal(String(c)); onChange(c / 100);
  };
  const liveChange = (raw: string) => {
    setLocal(raw);
    const pct = parseInt(raw, 10);
    if (isNaN(pct)) return;
    const c = Math.max(0, Math.min(100, pct));
    onChange(c / 100);
  };
  return (
    <label style={{ ...dimLabelCss }}>
      <span style={{ color: TXT.label, fontSize: 12 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <input
          type="number" value={local} min={0} max={100}
          onChange={e => liveChange(e.target.value)}
          onBlur={commit} onKeyDown={e => e.key === 'Enter' && commit()}
          style={{ ...inputCss, width: FIELD_W, height: INPUT_H, padding: '0 4px', fontSize: 12, fontFamily: 'monospace', boxSizing: 'border-box' }}
        />
        <span style={{ fontSize: 12, color: TXT.help, flexShrink: 0 }}>%</span>
      </div>
    </label>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// IntInput — largeur uniforme FIELD_W
// ══════════════════════════════════════════════════════════════════════════════

interface IntInputProps {
  label: string; value: number; min?: number; max?: number; unit?: string;
  onChange: (v: number) => void;
}

const IntInput: React.FC<IntInputProps> = ({
  label, value, min = 0, max, unit, onChange,
}) => {
  const [local, setLocal] = useState(String(value));
  const prev = useRef(value);
  if (prev.current !== value) { prev.current = value; setLocal(String(value)); }
  const commit = () => {
    const n = parseInt(local, 10);
    if (isNaN(n)) { setLocal(String(value)); return; }
    const c = Math.max(min, max !== undefined ? Math.min(max, n) : n);
    setLocal(String(c)); onChange(c);
  };
  const liveChange = (raw: string) => {
    setLocal(raw);
    const n = parseInt(raw, 10);
    if (isNaN(n)) return;
    const c = Math.max(min, max !== undefined ? Math.min(max, n) : n);
    onChange(c);
  };
  return (
    <label style={{ ...dimLabelCss }}>
      <span style={{ color: TXT.label, fontSize: 12 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <input
          type="number" value={local} min={min} max={max}
          onChange={e => liveChange(e.target.value)}
          onBlur={commit} onKeyDown={e => e.key === 'Enter' && commit()}
          style={{ ...inputCss, width: FIELD_W, height: INPUT_H, padding: '0 4px', fontSize: 12, fontFamily: 'monospace', boxSizing: 'border-box' }}
        />
        {unit && <span style={{ fontSize: 12, color: TXT.help, flexShrink: 0 }}>{unit}</span>}
      </div>
    </label>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// LockChainButton
// ══════════════════════════════════════════════════════════════════════════════

interface LockChainButtonProps { locked: boolean; onToggle: () => void; title?: string; }

const LockChainButton: React.FC<LockChainButtonProps> = ({ locked, onToggle, title }) => (
  <button
    type="button"
    title={title ?? (locked ? 'Ratio verrouillé' : 'Ratio libre')}
    onClick={onToggle}
    style={{
      width: INPUT_H, height: INPUT_H, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 5, cursor: 'pointer', padding: 0,
      border: `1px solid ${locked ? 'rgba(96,165,250,0.5)' : 'rgba(255,255,255,0.2)'}`,
      background: locked ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.04)',
      color: locked ? '#93c5fd' : TXT.help,
      alignSelf: 'flex-end', transition: 'all 0.15s',
    }}
  >
    {locked ? (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M5.5 8.5L8.5 5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M3.5 6.5L2 8a2.121 2.121 0 003 3l1.5-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10.5 7.5L12 6a2.121 2.121 0 00-3-3L7.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ) : (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M5.5 8.5L6.3 7.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M7.7 6.3L8.5 5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M3.5 6.5L2 8a2.121 2.121 0 003 3l1.5-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10.5 7.5L12 6a2.121 2.121 0 00-3-3L7.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )}
  </button>
);

// ══════════════════════════════════════════════════════════════════════════════
// IconActionButton
// ══════════════════════════════════════════════════════════════════════════════

interface IconActionButtonProps {
  onClick: () => void; title: string; accentColor: string;
  children: React.ReactNode;
}

const IconActionButton: React.FC<IconActionButtonProps> = ({
  onClick, title, accentColor, children,
}) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    style={{
      width: INPUT_H, height: INPUT_H, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 5, cursor: 'pointer', padding: 0,
      border: `1px solid ${accentColor}50`,
      background: `${accentColor}12`,
      color: accentColor,
      alignSelf: 'flex-end', transition: 'all 0.15s',
    }}
  >
    {children}
  </button>
);

// ══════════════════════════════════════════════════════════════════════════════
// MuteButton
// ══════════════════════════════════════════════════════════════════════════════

interface MuteButtonProps { muted: boolean; onToggle: () => void; accentColor: string; }

const MuteButton: React.FC<MuteButtonProps> = ({ muted, onToggle, accentColor }) => (
  <button
    type="button"
    title={muted ? 'Son coupé — cliquer pour activer' : 'Son actif — cliquer pour couper'}
    onClick={onToggle}
    style={{
      width: INPUT_H, height: INPUT_H, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 5, cursor: 'pointer', padding: 0,
      border: `1px solid ${muted ? 'rgba(239,68,68,0.4)' : accentColor + '50'}`,
      background: muted ? 'rgba(239,68,68,0.12)' : `${accentColor}12`,
      color: muted ? '#fca5a5' : accentColor,
      transition: 'all 0.15s',
    }}
  >
    {muted ? (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M2 5H4L7 2.5V11.5L4 9H2V5Z" fill="currentColor"/>
        <line x1="9.5" y1="5" x2="12.5" y2="9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        <line x1="12.5" y1="5" x2="9.5" y2="9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ) : (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M2 5H4L7 2.5V11.5L4 9H2V5Z" fill="currentColor"/>
        <path d="M9 4.5C9.8 5.2 10.3 6 10.3 7C10.3 8 9.8 8.8 9 9.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        <path d="M10.5 3C11.9 4.1 12.7 5.5 12.7 7C12.7 8.5 11.9 9.9 10.5 11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    )}
  </button>
);

// ══════════════════════════════════════════════════════════════════════════════
// AudioMixerToggle
// ══════════════════════════════════════════════════════════════════════════════

interface AudioMixerToggleProps { active: boolean; onToggle: () => void; accentColor: string; }

const AudioMixerToggle: React.FC<AudioMixerToggleProps> = ({ active, onToggle, accentColor }) => (
  <button
    type="button"
    onClick={onToggle}
    title={active ? 'Audio → mixer (actif)' : 'Audio → mixer (inactif)'}
    style={{
      width: INPUT_H, height: INPUT_H, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 5, cursor: 'pointer', padding: 0,
      background: active ? `${accentColor}22` : 'rgba(255,255,255,0.04)',
      border: `1px solid ${active ? accentColor + '60' : 'rgba(255,255,255,0.18)'}`,
      color: active ? accentColor : TXT.help,
      transition: 'all 0.15s', position: 'relative',
    }}
  >
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2 5H4L7 2.5V11.5L4 9H2V5Z" fill="currentColor"/>
      {active ? (
        <>
          <path d="M9 4.5C9.8 5.2 10.3 6 10.3 7C10.3 8 9.8 8.8 9 9.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
          <path d="M10.5 3C11.9 4.1 12.7 5.5 12.7 7C12.7 8.5 11.9 9.9 10.5 11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        </>
      ) : (
        <>
          <line x1="9.5" y1="5" x2="12.5" y2="9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="12.5" y1="5" x2="9.5" y2="9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        </>
      )}
    </svg>
    {active && (
      <span style={{
        position: 'absolute', top: -2, right: -2,
        width: 5, height: 5, borderRadius: '50%',
        background: accentColor, border: '1px solid #0f1117',
      }} />
    )}
  </button>
);

// ══════════════════════════════════════════════════════════════════════════════
// ThumbnailGrid — sélecteur visuel 4 × N lignes
// ══════════════════════════════════════════════════════════════════════════════

interface ThumbnailGridProps {
  slots: { id: string; url: string; label: string }[];
  selectedIdx: number;
  accentColor: string;
  onSelect: (idx: number) => void;
  mediaType?: 'image' | 'video';
}

const ThumbnailGrid: React.FC<ThumbnailGridProps> = ({
  slots, selectedIdx, accentColor, onSelect, mediaType
}) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 4,
  }}>
    {slots.map((slot, idx) => {
      const selected = idx === selectedIdx;
      return (
        <div
          key={slot.id}
          onClick={() => onSelect(idx)}
          title={slot.label || `Slot ${idx + 1}`}
          style={{
            aspectRatio: '16/9',
            borderRadius: 4,
            border: `${selected ? 2 : 1}px solid ${selected ? accentColor : 'rgba(255,255,255,0.1)'}`,
            background: selected ? accentColor + '15' : 'rgba(255,255,255,0.04)',
            cursor: 'pointer',
            overflow: 'hidden',
            position: 'relative',
            transition: 'border-color 0.12s, background 0.12s',
            boxShadow: selected ? `0 0 6px ${accentColor}40` : 'none',
          }}
        >
          {slot.url ? (
            mediaType === 'video' ? (
              <VideoThumb
                url={slot.url}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <img
                src={slot.url}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            )
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, color: TXT.faint, fontStyle: 'italic',
            }}>
              vide
            </div>
          )}
          {slot.label && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              fontSize: 12,
              color: selected ? accentColor : TXT.label,
              background: 'rgba(0,0,0,0.6)',
              padding: '1px 3px',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              textAlign: 'center',
            }}>
              {slot.label}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// ScreenSlotSelector — sélecteur visuel des 4 emplacements Copie d'écran, avec
// vignette vidéo live (pas de fichier/url comme ThumbnailGrid : un flux getDisplayMedia)
// ══════════════════════════════════════════════════════════════════════════════

interface ScreenSlotSelectorProps {
  slots: ScreenShareSlot[];
  streams: Map<string, MediaStream>;
  selectedIdx: number;
  accentColor: string;
  onSelect: (idx: number) => void;
}

const ScreenSlotSelector: React.FC<ScreenSlotSelectorProps> = ({
  slots, streams, selectedIdx, accentColor, onSelect,
}) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
    {slots.map((slot, idx) => {
      const selected = idx === selectedIdx;
      const stream   = streams.get(slot.id);
      const active   = !!(stream?.active && stream.getVideoTracks()[0]?.readyState === 'live');
      return (
        <div
          key={slot.id}
          onClick={() => onSelect(idx)}
          title={slot.label}
          style={{
            aspectRatio: '16/9',
            borderRadius: 4,
            border: `${selected ? 2 : 1}px solid ${selected ? accentColor : 'rgba(255,255,255,0.1)'}`,
            background: selected ? accentColor + '15' : 'rgba(255,255,255,0.04)',
            cursor: 'pointer',
            overflow: 'hidden',
            position: 'relative',
            transition: 'border-color 0.12s, background 0.12s',
            boxShadow: selected ? `0 0 6px ${accentColor}40` : 'none',
          }}
        >
          {active ? (
            <DeviceStreamPreview stream={stream} />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, color: TXT.faint, fontStyle: 'italic',
            }}>
              vide
            </div>
          )}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            fontSize: 12,
            color: selected ? accentColor : TXT.label,
            background: 'rgba(0,0,0,0.6)',
            padding: '1px 3px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            textAlign: 'center',
          }}>
            {slot.label}
          </div>
        </div>
      );
    })}
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// PositionSizeGrid
// ══════════════════════════════════════════════════════════════════════════════

interface PositionSizeGridProps {
  x: number; y: number; zIndex: number; opacity: number;
  width: number; height: number;
  locked: boolean; onToggleLock: () => void;
  onX: (v: number) => void; onY: (v: number) => void;
  onZ: (v: number) => void; onOpacity: (v: number) => void;
  onWidth: (v: number) => void; onHeight: (v: number) => void;
  onNative?: () => void;
  onAdapt?: () => void;
  onFullscreen?: () => void;
  nativeSize?: { w: number; h: number } | null;
  accentColor?: string;
  // Bornes de X/Y en % du canvas de référence — étendues au-delà de [0, 100] pour les
  // scènes (incrustation partiellement/totalement hors-cadre par n'importe quel bord),
  // [0, 100] ailleurs par défaut.
  minXPct?: number;
  minYPct?: number;
  maxXPct?: number;
  maxYPct?: number;
  maxZ?: number;
}

const PositionSizeGrid: React.FC<PositionSizeGridProps> = ({
  x, y, zIndex, opacity, width, height,
  locked, onToggleLock,
  onX, onY, onZ, onOpacity, onWidth, onHeight,
  onNative, onAdapt, onFullscreen, nativeSize,
  accentColor = PAL.image.text,
  minXPct = 0, minYPct = 0, maxXPct = 100, maxYPct = 100, maxZ = 99,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Ligne 1 : X | Y | Z-index | Opacité */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
        <PxInput label="X" valuePct={x} refDim={REF_W} min={pctToPx(minXPct, REF_W)} max={pctToPx(maxXPct, REF_W)} onChange={onX} />
        <PxInput label="Y" valuePct={y} refDim={REF_H} min={pctToPx(minYPct, REF_H)} max={pctToPx(maxYPct, REF_H)} onChange={onY} />
        <IntInput label="Z-index" value={zIndex} min={0} max={maxZ} onChange={onZ} />
        <PctInput label="Opacité" value={opacity} onChange={onOpacity} />
      </div>

      {/* Ligne 2 : Largeur | Hauteur | boutons */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
        <PxInput label="Largeur" valuePct={width} refDim={REF_W} min={1} onChange={onWidth} />
        <PxInput label="Hauteur" valuePct={height} refDim={REF_H} min={1} onChange={onHeight} />

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, flex: 0, minWidth: 0 }}>
          <label style={{ ...dimLabelCss }}>
            <span style={{ color: 'rgba(255,255,255,0)', fontSize: 12, userSelect: 'none' }}>·</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <LockChainButton locked={locked} onToggle={onToggleLock} />
              {nativeSize && onNative && (
                <IconActionButton
                  onClick={onNative}
                  title={`Taille native : ${nativeSize.w}×${nativeSize.h} px`}
                  accentColor={PAL.layout.text}
                >
                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                    <rect x="1.5" y="1.5" width="11" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
                    <rect x="4" y="4" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.1"/>
                  </svg>
                </IconActionButton>
              )}
              {onAdapt && (
                <IconActionButton
                  onClick={onAdapt}
                  title="Adapter au ratio de l'image"
                  accentColor={PAL.image.text}
                >
                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                    <path d="M2 5V2h3M9 2h3v3M12 9v3H9M5 12H2V9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="4.5" y="4.5" width="5" height="5" rx="0.8" stroke="currentColor" strokeWidth="1.1"/>
                  </svg>
                </IconActionButton>
              )}
              {onFullscreen && (
                <IconActionButton
                  onClick={onFullscreen}
                  title="Plein écran 1920×1080"
                  accentColor={PAL.screen.text}
                >
                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                    <path d="M2 5V2h3M9 2h3v3M12 9v3H9M5 12H2V9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </IconActionButton>
              )}
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// RawPreview
// ══════════════════════════════════════════════════════════════════════════════

interface RawPreviewProps { url: string; type?: 'image' | 'video'; }

const RawPreview: React.FC<RawPreviewProps> = ({ url, type = 'image' }) => (
  <div style={{
    width: '100%', aspectRatio: '16/9',
    background: '#000',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 5,
    overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    {url ? (
      type === 'video' ? (
        <MediaboardVideo src={url} muted style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : (
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      )
    ) : (
      <span style={{ fontSize: 12, color: TXT.faint, fontStyle: 'italic' }}>Aucun aperçu</span>
    )}
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// VideoThumb — miniature d'une vidéo (seek à 0.5 s pour avoir une frame)
// ══════════════════════════════════════════════════════════════════════════════

const VideoThumb: React.FC<{ url: string; style: React.CSSProperties }> = ({ url, style }) => {
  const ref = useRef<HTMLVideoElement>(null);
  // Vidéo HLS du compte : sa vignette publique, plutôt qu'un lecteur par case.
  const poster = isHlsUrl(url) ? posterOfHlsUrl(url) : null;
  useEffect(() => {
    const v = ref.current;
    if (!v || !url || poster) return;
    const onMeta = () => { v.currentTime = 0.5; };
    v.addEventListener('loadedmetadata', onMeta);
    return () => v.removeEventListener('loadedmetadata', onMeta);
  }, [url, poster]);
  if (poster) return <img src={poster} alt="" style={style} />;
  return <MediaboardVideo ref={ref} src={url} muted playsInline preload="metadata" style={style} />;
};

// ══════════════════════════════════════════════════════════════════════════════
// JingleVideoPlayer — image+play puis player inline
// ══════════════════════════════════════════════════════════════════════════════

interface JingleVideoPlayerProps { url: string; accentColor: string; }

const JingleVideoPlayer: React.FC<JingleVideoPlayerProps> = ({ url, accentColor }) => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => { setPlaying(false); }, [url]);

  const handlePlay = () => { setPlaying(true); setTimeout(() => videoRef.current?.play(), 50); };
  const handleEnded = () => { setPlaying(false); };

  if (!url) return null;

  return (
    <div style={{
      width: '100%', aspectRatio: '16/9',
      background: '#000',
      border: `1px solid ${playing ? accentColor + '60' : 'rgba(255,255,255,0.08)'}`,
      borderRadius: 5,
      overflow: 'hidden',
      position: 'relative',
      transition: 'border-color 0.2s',
    }}>
      {playing ? (
        <MediaboardVideo
          ref={videoRef}
          src={url}
          controls
          onEnded={handleEnded}
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : (
        <>
          <VideoThumb
            url={url}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', opacity: 0.7 }}
          />
          <div
            onClick={handlePlay}
            style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', background: 'rgba(0,0,0,0.4)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.25)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.4)')}
          >
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: accentColor + '22',
              border: `2px solid ${accentColor}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'transform 0.15s',
            }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M6 4L14 9L6 14V4Z" fill={accentColor} />
              </svg>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// CanvasPreviewInteractive
// ══════════════════════════════════════════════════════════════════════════════

interface CanvasItem {
  id: string; url: string; label: string;
  x: number; y: number; width: number; height: number;
  opacity: number; zIndex?: number; accentColor?: string;
  mediaType?: 'image' | 'video';
}

type ResizeCorner = 'nw' | 'ne' | 'sw' | 'se';

// Redimensionne un cadre en ancrant le coin opposé à celui saisi.
// `hPerW` : rapport hauteur/largeur imposé (ratio verrouillé), null si libre.
function computeCornerResize(
  corner: ResizeCorner,
  o: { x: number; y: number; w: number; h: number },
  dx: number, dy: number,
  minB: number, maxB: number,
  hPerW: number | null,
): { x: number; y: number; width: number; height: number } {
  const left = corner[1] === 'w';
  const top = corner[0] === 'n';
  const maxW = Math.max(3, left ? o.x + o.w - minB : maxB - o.x);
  const maxH = Math.max(3, top ? o.y + o.h - minB : maxB - o.y);
  let w = Math.max(3, Math.min(maxW, left ? o.w - dx : o.w + dx));
  let h: number;
  if (hPerW) {
    h = w * hPerW;
    if (h > maxH) { h = maxH; w = h / hPerW; }
    if (h < 3) { h = 3; w = h / hPerW; }
  } else {
    h = Math.max(3, Math.min(maxH, top ? o.h - dy : o.h + dy));
  }
  return {
    x: left ? o.x + o.w - w : o.x,
    y: top ? o.y + o.h - h : o.y,
    width: w, height: h,
  };
}

const RESIZE_CORNERS: { c: ResizeCorner; pos: React.CSSProperties; cursor: string; radius: string }[] = [
  { c: 'nw', pos: { top: -1, left: -1 },     cursor: 'nwse-resize', radius: '3px 0 2px 0' },
  { c: 'ne', pos: { top: -1, right: -1 },    cursor: 'nesw-resize', radius: '0 3px 0 2px' },
  { c: 'sw', pos: { bottom: -1, left: -1 },  cursor: 'nesw-resize', radius: '0 2px 0 3px' },
  { c: 'se', pos: { bottom: -1, right: -1 }, cursor: 'nwse-resize', radius: '2px 0 3px 0' },
];

interface CanvasPreviewInteractiveProps {
  items: CanvasItem[];
  onItemChange: (id: string, patch: Partial<CanvasItem>) => void;
  highlightId: string | null;
  setHighlight: (id: string | null) => void;
  lockedRatios: Record<string, boolean>;
  ratios: Record<string, number>;
}

const CanvasPreviewInteractive: React.FC<CanvasPreviewInteractiveProps> = ({
  items, onItemChange, highlightId, setHighlight, lockedRatios, ratios,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cW, setCW] = useState(380);
  const cH = Math.round(cW * REF_H / REF_W);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setCW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scaleX = cW / 100;
  const scaleY = cH / 100;

  const dragRef = useRef<{
    id: string; mode: 'move' | 'resize'; corner: ResizeCorner;
    startX: number; startY: number;
    origX: number; origY: number; origW: number; origH: number;
  } | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const isLocked = useCallback(
    (id: string) => lockedRatios[id] !== false,
    [lockedRatios],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, id: string, mode: 'move' | 'resize', corner: ResizeCorner = 'se') => {
      e.preventDefault(); e.stopPropagation();
      const item = items.find(s => s.id === id);
      if (!item) return;
      setSelectedId(id);
      dragRef.current = {
        id, mode, corner,
        startX: e.clientX, startY: e.clientY,
        origX: item.x, origY: item.y, origW: item.width, origH: item.height,
      };
      const handleMove = (ev: MouseEvent) => {
        const d = dragRef.current;
        if (!d) return;
        const dx = (ev.clientX - d.startX) / scaleX;
        const dy = (ev.clientY - d.startY) / scaleY;
        if (d.mode === 'move') {
          onItemChange(d.id, {
            x: Math.max(0, Math.min(100 - d.origW, d.origX + dx)),
            y: Math.max(0, Math.min(100 - d.origH, d.origY + dy)),
          });
        } else {
          const ratio = ratios[d.id];
          onItemChange(d.id, computeCornerResize(
            d.corner,
            { x: d.origX, y: d.origY, w: d.origW, h: d.origH },
            dx, dy, 0, 100,
            isLocked(d.id) && ratio ? REF_W / (ratio * REF_H) : null,
          ));
        }
      };
      const handleUp = () => {
        dragRef.current = null;
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
      };
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
    },
    [items, onItemChange, scaleX, scaleY, ratios, isLocked],
  );

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <div
        onClick={() => setSelectedId(null)}
        style={{
          width: cW, height: cH,
          background: '#000',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 4, position: 'relative', overflow: 'hidden', cursor: 'default',
        }}
      >
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.05,
          backgroundImage: [
            'linear-gradient(rgba(255,255,255,0.5) 1px,transparent 1px)',
            'linear-gradient(90deg,rgba(255,255,255,0.5) 1px,transparent 1px)',
          ].join(','),
          backgroundSize: `${cW / 8}px ${cH / 4.5}px`,
        }} />
        {[...items].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0)).map(item => {
          if (!item.url) return null;
          const sel = selectedId === item.id;
          const hl = highlightId === item.id;
          const color = item.accentColor ?? PAL.image.stroke;
          const textColor = item.accentColor ? item.accentColor + 'cc' : PAL.image.text;
          const left = item.x / 100 * cW;
          const top  = item.y / 100 * cH;
          const w    = Math.max(4, item.width  / 100 * cW);
          const h    = Math.max(4, item.height / 100 * cH);
          return (
            <div
              key={item.id}
              onClick={e => { e.stopPropagation(); setSelectedId(item.id); setHighlight(item.id); }}
              onMouseDown={e => handleMouseDown(e, item.id, 'move')}
              onMouseEnter={() => setHighlight(item.id)}
              onMouseLeave={() => !dragRef.current && setHighlight(null)}
              style={{
                position: 'absolute', left, top, width: w, height: h,
                border: `${sel || hl ? 2 : 1}px solid ${sel ? textColor : color + (hl ? '' : '99')}`,
                borderRadius: 2,
                boxShadow: sel ? `0 0 8px ${color}70` : hl ? `0 0 5px ${color}40` : 'none',
                cursor: 'move',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
                zIndex: sel ? 10 : (item.zIndex ?? 1),
                opacity: item.opacity,
              }}
            >
              {item.mediaType === 'video' ? (
                <VideoThumb
                  url={item.url}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', inset: 0, pointerEvents: 'none' as any }}
                />
              ) : (
                <img src={item.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', inset: 0, pointerEvents: 'none' }} />
              )}
              {isLocked(item.id) && ratios[item.id] !== undefined && (
                <div style={{ position: 'absolute', top: 2, right: 3, fontSize: 12, color: '#93c5fd', pointerEvents: 'none' }}>🔗</div>
              )}
              {sel && RESIZE_CORNERS.map(k => (
                <div
                  key={k.c}
                  onMouseDown={e => handleMouseDown(e, item.id, 'resize', k.c)}
                  style={{ position: 'absolute', ...k.pos, width: 12, height: 12, cursor: k.cursor, background: color, borderRadius: k.radius, opacity: 0.9, zIndex: 2 }}
                />
              ))}
            </div>
          );
        })}
        <div style={{ position: 'absolute', bottom: 2, right: 4, fontSize: 12, color: TXT.faint, fontFamily: 'monospace', pointerEvents: 'none' }}>
          {REF_W}×{REF_H}
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// TimecodeInput
// ══════════════════════════════════════════════════════════════════════════════

interface TimecodeInputProps {
  label: string;
  sec: number;
  duration: number;
  accentColor: string;
  onChange: (pct: number) => void;
  minPct: number;
  maxPct: number;
}

const TimecodeInput: React.FC<TimecodeInputProps> = ({
  label, sec, duration, accentColor, onChange, minPct, maxPct,
}) => {
  const [local, setLocal] = useState(fmtTimecode(sec));
  const prev = useRef(sec);
  if (Math.abs(prev.current - sec) > 0.05) {
    prev.current = sec;
    setLocal(fmtTimecode(sec));
  }
  const parseTimecode = (s: string): number | null => {
    const m = s.match(/^(\d+):(\d{2})\.(\d)$/);
    if (!m) return null;
    return parseInt(m[1]) * 60 + parseInt(m[2]) + parseInt(m[3]) / 10;
  };
  const commit = () => {
    const parsed = parseTimecode(local);
    if (parsed === null || duration === 0) { setLocal(fmtTimecode(sec)); return; }
    const newPct = Math.max(minPct, Math.min(maxPct, (parsed / duration) * 100));
    onChange(newPct);
  };
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 12, color: TXT.help, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </span>
      <input
        type="text"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => e.key === 'Enter' && commit()}
        placeholder="00:00.0"
        style={{ ...inputCss, fontFamily: 'monospace', fontSize: 12, height: INPUT_H, textAlign: 'center', color: accentColor, boxSizing: 'border-box' }}
      />
      <span style={{ fontSize: 12, color: TXT.help, fontFamily: 'monospace', textAlign: 'center' }}>
        {duration > 0 ? `${sec.toFixed(1)} s` : '—'}
      </span>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// ScreenShareConfigPanel
// ══════════════════════════════════════════════════════════════════════════════

interface ScreenShareConfigPanelProps {
  label: string; placeholder: string; config: ScreenShareConfig; streamActive: boolean; screenShareStream: MediaStream | null;
  onLabelChange: (label: string) => void;
  onConfigChange: (c: ScreenShareConfig) => void; onResetStream: () => Promise<void>;
}

const ScreenShareConfigPanel: React.FC<ScreenShareConfigPanelProps> = ({
  label, placeholder, config, streamActive, screenShareStream, onLabelChange, onConfigChange, onResetStream,
}) => {
  const [resetting, setResetting] = useState(false);
  const handleReset = async () => {
    setResetting(true);
    try { await onResetStream(); } finally { setResetting(false); }
  };
  const set = (p: Partial<ScreenShareConfig>) => onConfigChange({ ...config, ...p });

  // Ce qui a réellement été choisi dans le sélecteur du navigateur (écran entier / fenêtre /
  // onglet, + le libellé de la track si le navigateur le fournit) — la seule info dont on
  // dispose côté web, getDisplayMedia ne renvoie jamais le nom exact de l'appli pour les cas
  // "Fenêtre" par souci de vie privée (dépend du navigateur/OS).
  const captureTrack    = streamActive ? screenShareStream?.getVideoTracks()[0] : undefined;
  const captureSettings = captureTrack?.getSettings() as (MediaTrackSettings & { displaySurface?: string }) | undefined;
  const captureSurface  = captureSettings?.displaySurface === 'monitor' ? 'Écran entier'
    : captureSettings?.displaySurface === 'window' ? 'Fenêtre'
    : captureSettings?.displaySurface === 'browser' ? 'Onglet de navigateur'
    : null;
  const captureDetail = captureTrack?.label || null;
  const captureLabel = captureSurface && captureDetail && captureDetail !== captureSurface
    ? `${captureSurface} — ${captureDetail}`
    : captureSurface ?? captureDetail;

  const rowCss: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.05)',
  };
  const selectCss: React.CSSProperties = {
    ...inputCss, width: 'auto', minWidth: 120, padding: '5px 10px', fontSize: 12,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 12, color: TXT.label, flexShrink: 0 }}>Nom :</span>
        <input
          type="text"
          value={label}
          placeholder={placeholder}
          onChange={e => onLabelChange(e.target.value)}
          style={{ ...inputCss, flex: 1, height: INPUT_H, boxSizing: 'border-box' }}
        />
        <button
          type="button"
          onClick={() => onLabelChange('')}
          title="Effacer le nom"
          style={{
            width: INPUT_H, height: INPUT_H, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6, cursor: 'pointer', padding: 0,
            border: '1px solid rgba(239,68,68,0.35)',
            background: 'rgba(239,68,68,0.08)',
            color: '#fca5a5',
          }}
        >
          <Trash2 size={12} />
        </button>
      </div>

      <div style={{
        display: 'flex', flexDirection: 'column', gap: 10,
        padding: '12px 14px',
        background: streamActive ? 'rgba(52,211,153,0.08)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${streamActive ? 'rgba(52,211,153,0.25)' : 'rgba(255,255,255,0.07)'}`,
        borderRadius: 10,
      }}>
        <button
          type="button"
          onClick={handleReset}
          disabled={resetting}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8, cursor: resetting ? 'wait' : 'pointer',
            fontSize: 12, fontWeight: 600,
            border: `1px solid ${PAL.screen.stroke}50`,
            background: `${PAL.screen.stroke}18`,
            color: PAL.screen.text,
          }}
        >
          <Monitor size={13} /> Choisir une source
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: streamActive ? '#34d399' : '#475569', boxShadow: streamActive ? '0 0 6px #34d399' : 'none', display: 'inline-block', flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: streamActive ? '#6ee7b7' : TXT.body }}>
            {streamActive ? (captureLabel ?? "Flux d'écran actif") : "Aucun flux d'écran"}
          </span>
        </div>
      </div>

      {streamActive && (
        <>
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={rowCss}>
              <span style={{ fontSize: 12, color: '#fff' }}>Fréquence d'images</span>
              <select value={config.frameRate} onChange={e => set({ frameRate: parseInt(e.target.value) as any })} style={selectCss}>
                <option value={15}>15 fps</option><option value={30}>30 fps</option><option value={60}>60 fps</option>
              </select>
            </div>
            <div style={rowCss}>
              <span style={{ fontSize: 12, color: '#fff' }}>Résolution max</span>
              <select
                value={`${config.width}x${config.height}`}
                onChange={e => { const [w, h] = e.target.value.split('x').map(Number); set({ width: w, height: h }); }}
                style={selectCss}
              >
                <option value="1280x720">720p</option><option value="1920x1080">1080p</option>
                <option value="2560x1440">1440p</option><option value="3840x2160">4K</option>
              </select>
            </div>
            <div style={rowCss}>
              <span style={{ fontSize: 12, color: '#fff' }}>Curseur souris</span>
              <select value={config.cursor} onChange={e => set({ cursor: e.target.value as any })} style={selectCss}>
                <option value="always">Toujours</option><option value="motion">Si en mouvement</option><option value="never">Jamais</option>
              </select>
            </div>
            <div style={{ ...rowCss, borderBottom: 'none' }}>
              <span style={{ fontSize: 12, color: '#fff' }}>Audio système</span>
              <div onClick={() => set({ audio: !config.audio })} style={{ width: 40, height: 22, borderRadius: 11, cursor: 'pointer', background: config.audio ? PAL.screen.stroke : 'rgba(255,255,255,0.12)', position: 'relative', transition: 'all 0.2s' }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: config.audio ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }} />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => onConfigChange(DEFAULT_SCREEN_SHARE_CONFIG)}
              style={{ fontSize: 12, padding: '5px 12px', borderRadius: 7, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: TXT.body }}
            >
              ↺ Réinitialiser
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SceneConfigPanel
// ══════════════════════════════════════════════════════════════════════════════

const INCRUST_COLORS: Record<LayoutIncrustation['type'], { stroke: string; fill: string; text: string }> = {
  image:  { stroke: '#22d3ee', fill: 'rgba(34,211,238,0.3)',  text: '#a5f3fc' },
  video:  { stroke: '#f87171', fill: 'rgba(248,113,113,0.3)', text: '#fca5a5' },
  source: { stroke: '#4ade80', fill: 'rgba(74,222,128,0.3)',  text: '#86efac' },
};

const BACKGROUND_TYPE_OPTIONS: { value: LayoutBackgroundType; label: string }[] = [
  { value: 'none',   label: '— Aucun —' },
  { value: 'image',  label: '🖼 Image fixe' },
  { value: 'video',  label: '🎬 Vidéo en boucle' },
  { value: 'source', label: '📹 Source vidéo live' },
];

const INCRUST_TYPE_OPTIONS: { value: LayoutIncrustation['type']; label: string }[] = [
  { value: 'image',  label: '🖼 Image' },
  { value: 'video',  label: '🎬 Vidéo' },
  { value: 'source', label: '📹 Source' },
];

let _incrustIdCounter = Date.now();
const nextIncrustId = () => `incrust-${++_incrustIdCounter}`;

interface SceneThumbnailGridProps {
  slots: LayoutSlot[];
  selectedIdx: number;
  onSelect: (idx: number) => void;
}

const SceneThumbnailGrid: React.FC<SceneThumbnailGridProps> = ({
  slots, selectedIdx, onSelect,
}) => {
  const accentColor = PAL.layout.stroke;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: 4,
    }}>
      {slots.map((slot, idx) => {
        const selected  = idx === selectedIdx;
        const hasBg     = slot.background.type !== 'none' && !!slot.background.url;
        const hasSource = slot.background.type === 'source';
        const hasIncr   = slot.incrustations.length > 0;
        const hasContent = hasBg || hasSource || hasIncr;

        return (
          <div
            key={slot.id}
            onClick={() => onSelect(idx)}
            title={slot.label || `Scène ${idx + 1}`}
            style={{
              aspectRatio: '16/9',
              borderRadius: 4,
              border: `${selected ? 2 : 1}px solid ${selected ? accentColor : 'rgba(255,255,255,0.1)'}`,
              background: selected ? accentColor + '15' : 'rgba(255,255,255,0.04)',
              cursor: 'pointer',
              overflow: 'hidden',
              position: 'relative',
              transition: 'border-color 0.12s, background 0.12s',
              boxShadow: selected ? `0 0 6px ${accentColor}40` : 'none',
            }}
          >
            {hasBg && slot.background.type === 'image' && (
              <img
                src={slot.background.url}
                alt=""
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
            {hasBg && slot.background.type === 'video' && (
              <VideoThumb
                url={slot.background.url}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
            {hasSource && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(74,222,128,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 12, color: '#86efac' }}>📹</span>
              </div>
            )}
            {hasIncr && slot.incrustations.map(ic => {
              const col = INCRUST_COLORS[ic.type];
              return (
                <div
                  key={ic.id}
                  style={{
                    position: 'absolute',
                    left: `${ic.x}%`, top: `${ic.y}%`,
                    width: `${ic.width}%`, height: `${ic.height}%`,
                    border: `1px solid ${col.stroke}`,
                    background: col.fill,
                    borderRadius: 1,
                    opacity: ic.opacity,
                  }}
                />
              );
            })}
            {!hasContent && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: TXT.faint, fontStyle: 'italic' }}>
                vide
              </div>
            )}
            {(slot.label || hasContent) && (
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                fontSize: 12,
                color: selected ? accentColor : TXT.label,
                background: 'rgba(0,0,0,0.65)',
                padding: '1px 3px',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                textAlign: 'center',
              }}>
                {slot.label || `Scène ${idx + 1}`}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

interface SceneConfigPanelProps {
  layoutSlots: LayoutSlot[];
  availableSources: VideoSource[];
  onLayoutChange: (slots: LayoutSlot[]) => void;
  initialLayoutIdx?: number;
}

const SceneConfigPanel: React.FC<SceneConfigPanelProps> = ({
  layoutSlots, availableSources, onLayoutChange, initialLayoutIdx,
}) => {
  const { registry } = useSourceRegistry();

  const [selectedLayoutIdx,  setSelectedLayoutIdx]  = useState(initialLayoutIdx ?? 0);
  const [selectedIncrustId,  setSelectedIncrustId]  = useState<string | null>(null);
  // Confirmation par le composant Window de l'application, pas par window.confirm.
  const [showResetSceneConfirm, setShowResetSceneConfirm] = useState(false);
  const [newIncrustType,     setNewIncrustType]      = useState<LayoutIncrustation['type']>('image');
  const [bgFileNames,        setBgFileNames]         = useState<Record<number, string>>({});
  const [incrustFileNames,   setIncrustFileNames]    = useState<Record<string, string>>({});
  const [lockedRatios,       setLockedRatios]        = useState<Record<string, boolean>>({});
  const [incrustRatios,      setIncrustRatios]       = useState<Record<string, number>>({});
  const [incrustNativeSizes, setIncrustNativeSizes]  = useState<Record<string, { w: number; h: number }>>({});
  const [bgNativeSizes,      setBgNativeSizes]        = useState<Record<number, { w: number; h: number }>>({});

  const rightColRef = useRef<HTMLDivElement>(null);
  const [wysiW, setWysiW] = useState(380);
  const wysiH = Math.round(wysiW * REF_H / REF_W);

  useEffect(() => {
    const el = rightColRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWysiW(Math.max(200, el.clientWidth - 28)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scaleX = wysiW / 100;
  const scaleY = wysiH / 100;

  const dragRef = useRef<{
    id: string; mode: 'move' | 'resize'; corner: ResizeCorner;
    startX: number; startY: number;
    origX: number; origY: number; origW: number; origH: number;
  } | null>(null);

  const isLocked   = useCallback((id: string) => lockedRatios[id] !== false, [lockedRatios]);
  const toggleLock = useCallback((id: string) => setLockedRatios(prev => ({ ...prev, [id]: !isLocked(id) })), [isLocked]);

  const handleWysiMouseDown = useCallback(
    (e: React.MouseEvent, id: string, mode: 'move' | 'resize', corner: ResizeCorner = 'se') => {
      const currentSlot = layoutSlots[selectedLayoutIdx];
      if (!currentSlot) return;
      const ic = currentSlot.incrustations.find(i => i.id === id);
      if (!ic) return;
      e.preventDefault(); e.stopPropagation();
      setSelectedIncrustId(id);
      dragRef.current = {
        id, mode, corner,
        startX: e.clientX, startY: e.clientY,
        origX: ic.x, origY: ic.y, origW: ic.width, origH: ic.height,
      };
      const doUpd = (incrustId: string, p: Partial<LayoutIncrustation>) =>
        onLayoutChange(layoutSlots.map((s, j) => j === selectedLayoutIdx
          ? { ...s, incrustations: s.incrustations.map(i => i.id === incrustId ? { ...i, ...p } : i) }
          : s
        ));
      const handleMove = (ev: MouseEvent) => {
        const d = dragRef.current; if (!d) return;
        const dx = (ev.clientX - d.startX) / scaleX;
        const dy = (ev.clientY - d.startY) / scaleY;
        if (d.mode === 'move') {
          // Bornes -100% à 200% : une scène peut avoir des incrustations partiellement ou
          // totalement hors-cadre, aussi bien par la gauche/le haut que par la droite/le bas
          // (ex: entrée en glissement, cadrage volontaire).
          doUpd(d.id, {
            x: Math.max(-100, Math.min(200, d.origX + dx)),
            y: Math.max(-100, Math.min(200, d.origY + dy)),
          });
        } else {
          const ratio = incrustRatios[d.id];
          doUpd(d.id, computeCornerResize(
            d.corner,
            { x: d.origX, y: d.origY, w: d.origW, h: d.origH },
            dx, dy, -100, 200,
            isLocked(d.id) && ratio ? REF_W / (ratio * REF_H) : null,
          ));
        }
      };
      const handleUp = () => {
        dragRef.current = null;
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
      };
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
    },
    [layoutSlots, selectedLayoutIdx, onLayoutChange, scaleX, scaleY, incrustRatios, isLocked],
  );

  const slot = layoutSlots[selectedLayoutIdx];
  if (!slot) return null;

  const updSlot = (p: Partial<LayoutSlot>) =>
    onLayoutChange(layoutSlots.map((s, j) => j === selectedLayoutIdx ? { ...s, ...p } : s));

  const updBg = (p: Partial<LayoutBackground>) =>
    updSlot({ background: { ...slot.background, ...p } });

  const updIncrust = (id: string, p: Partial<LayoutIncrustation>) =>
    updSlot({ incrustations: slot.incrustations.map(ic => ic.id === id ? { ...ic, ...p } : ic) });

  const updIncrustWidth = (id: string, newWpct: number) => {
    const ratio = incrustRatios[id];
    if (isLocked(id) && ratio) {
      updIncrust(id, { width: newWpct, height: Math.max(0.5, (newWpct * REF_W) / (ratio * REF_H)) });
    } else updIncrust(id, { width: newWpct });
  };

  const updIncrustHeight = (id: string, newHpct: number) => {
    const ratio = incrustRatios[id];
    if (isLocked(id) && ratio) {
      updIncrust(id, { height: newHpct, width: Math.max(0.5, (newHpct * ratio * REF_H) / REF_W) });
    } else updIncrust(id, { height: newHpct });
  };

  const addIncrust = () => {
    const id = nextIncrustId();
    const newIc: LayoutIncrustation = {
      id, type: newIncrustType, url: '', sourceId: '', label: '',
      x: 5, y: 5, width: 25, height: 20, opacity: 1, zIndex: slot.incrustations.length + 1,
    };
    updSlot({ incrustations: [...slot.incrustations, newIc] });
    setSelectedIncrustId(id);
  };

  const removeIncrust = (id: string) => {
    updSlot({ incrustations: slot.incrustations.filter(ic => ic.id !== id) });
    if (selectedIncrustId === id) setSelectedIncrustId(null);
  };

  const resetSceneName = slot.label || `Scène ${selectedLayoutIdx + 1}`;

  const resetScene = () => {
    updSlot({ label: '', background: { type: 'none', url: '', sourceId: '' }, incrustations: [] });
    setBgFileNames(prev => { const n = { ...prev }; delete n[selectedLayoutIdx]; return n; });
    setBgNativeSizes(prev => { const n = { ...prev }; delete n[selectedLayoutIdx]; return n; });
    setSelectedIncrustId(null);
  };

  const selectedIncrust = slot.incrustations.find(ic => ic.id === selectedIncrustId) ?? null;

  // Le pipeline de compositing dessine le fond à sa taille de pixels réelle sur le canvas
  // de diffusion (résolution de référence REF_W×REF_H par défaut) — on met donc la taille
  // native à l'échelle de l'aperçu (wysiW/REF_W) pour que le rendu WYSIWYG soit fidèle.
  const previewScale = wysiW / REF_W;

  const renderBgPreview = () => {
    const native = bgNativeSizes[selectedLayoutIdx];
    if (slot.background.type === 'image' && slot.background.url) {
      if (!native)
        return <img src={slot.background.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', inset: 0 }} />;
      const rects = computeBgTileRects(slot.background.fit, native.w * previewScale, native.h * previewScale, wysiW, wysiH);
      return <>{rects.map((rect, i) => (
        <img key={i} src={slot.background.url} alt="" style={{ position: 'absolute', left: rect.x, top: rect.y, width: rect.w, height: rect.h }} />
      ))}</>;
    }
    if (slot.background.type === 'video' && slot.background.url) {
      if (!native)
        return <MediaboardVideo src={slot.background.url} autoPlay loop muted playsInline style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', inset: 0 }} />;
      const rects = computeBgTileRects(slot.background.fit, native.w * previewScale, native.h * previewScale, wysiW, wysiH);
      return <>{rects.map((rect, i) => (
        <MediaboardVideo key={i} src={slot.background.url} autoPlay loop muted playsInline style={{ position: 'absolute', left: rect.x, top: rect.y, width: rect.w, height: rect.h }} />
      ))}</>;
    }
    if (slot.background.type === 'source') {
      const src = availableSources.find(s => s.id === slot.background.sourceId);
      return (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(74,222,128,0.08)' }}>
          <span style={{ fontSize: 12, color: '#86efac', fontFamily: 'monospace' }}>{src?.label ?? 'Source'}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

      {showResetSceneConfirm && (
        <Window message                 = {<>{`Réinitialiser "${resetSceneName}" ? Cette action supprime le fond et toutes les incrustations.`}</>}
                messageType             = "question"
                onClickOnValidateButton = {() => { setShowResetSceneConfirm(false); resetScene(); }}
                onClickOnCancelButton   = {() => setShowResetSceneConfirm(false)}
                validButtonText         = "Oui"
                cancelButtonText        = "Non" />
      )}

      {/* ══ Colonne gauche ══ */}
      <div style={{
        width: 350, flexShrink: 0,
        borderRight: '1px solid rgba(255,255,255,0.06)',
        padding: '14px 14px',
        display: 'flex', flexDirection: 'column', gap: 10,
        overflowY: 'auto',
      }}>

        <SceneThumbnailGrid
          slots={layoutSlots}
          selectedIdx={selectedLayoutIdx}
          onSelect={idx => { setSelectedLayoutIdx(idx); setSelectedIncrustId(null); }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: TXT.label, flexShrink: 0 }}>Nom :</span>
          <input
            type="text"
            value={slot.label}
            placeholder={`Scène ${selectedLayoutIdx + 1}`}
            onChange={e => updSlot({ label: e.target.value })}
            style={{ ...inputCss, flex: 1, height: INPUT_H, boxSizing: 'border-box' }}
          />
          <button
            type="button"
            onClick={() => setShowResetSceneConfirm(true)}
            title="Réinitialiser cette scène"
            style={{
              width: INPUT_H, height: INPUT_H, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 6, cursor: 'pointer', padding: 0,
              border: '1px solid rgba(239,68,68,0.35)',
              background: 'rgba(239,68,68,0.08)',
              color: '#fca5a5',
            }}
          >
            <Trash2 size={12} />
          </button>
        </div>

        {/* Fond */}
        <div style={sectionBoxCss}>
          <div style={sectionLabelCss}>Fond</div>
          <select
            value={slot.background.type}
            onChange={e => {
              updBg({ type: e.target.value as LayoutBackgroundType, url: '', sourceId: '' });
              setBgNativeSizes(prev => { const n = { ...prev }; delete n[selectedLayoutIdx]; return n; });
            }}
            style={inputCss}
          >
            {BACKGROUND_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {slot.background.type === 'source' && (
            <select value={slot.background.sourceId} onChange={e => updBg({ sourceId: e.target.value })} style={inputCss}>
              <option value="">— Choisir —</option>
              {availableSources.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          )}
          {(slot.background.type === 'image' || slot.background.type === 'video') && (
            <div style={{ display: 'flex', gap: 6 }}>
              <FilePickerButton
                accept={slot.background.type === 'image' ? 'image/*' : 'video/*'}
                filename={slot.background.originalName ?? bgFileNames[selectedLayoutIdx] ?? ''}
                onFile={async (dataUrl, rawName) => {
                  const { mediaId, objectUrl } = await storeMediaSource(
                    dataUrl, rawName, 'scene', slot.id, 'background', slot.background.instantMediaId,
                  );
                  const layoutIdx = selectedLayoutIdx;
                  updBg({ url: objectUrl, instantMediaId: mediaId, originalName: rawName });
                  setBgFileNames(prev => ({ ...prev, [layoutIdx]: rawName }));
                  if (slot.background.type === 'image') {
                    const img = new window.Image();
                    img.onload = () => setBgNativeSizes(prev => ({ ...prev, [layoutIdx]: { w: img.naturalWidth, h: img.naturalHeight } }));
                    img.src = objectUrl;
                  } else {
                    const vid = document.createElement('video');
                    vid.preload = 'metadata';
                    vid.onloadedmetadata = () => setBgNativeSizes(prev => ({ ...prev, [layoutIdx]: { w: vid.videoWidth, h: vid.videoHeight } }));
                    setVideoSource(vid, objectUrl);
                    vid.addEventListener('loadedmetadata', () => releaseVideoSource(vid), { once: true });
                  }
                }}
                onDelete={slot.background.url ? async () => {
                  if (slot.background.instantMediaId) deleteMediaRecord(slot.background.instantMediaId);
                  updBg({ url: '', sourceId: '', instantMediaId: '', originalName: '' });
                  setBgFileNames(prev => { const n = { ...prev }; delete n[selectedLayoutIdx]; return n; });
                  setBgNativeSizes(prev => { const n = { ...prev }; delete n[selectedLayoutIdx]; return n; });
                } : undefined}
              />
            </div>
          )}
          {slot.background.type !== 'none' && (slot.background.url || slot.background.sourceId) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {BG_FIT_OPTIONS.map(o => {
                const active = (slot.background.fit ?? 'containXY') === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    title={`${o.label} — ${o.title}`}
                    onClick={() => updBg({ fit: o.value })}
                    style={{
                      width: INPUT_H, height: INPUT_H, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: 5, cursor: 'pointer', padding: 0,
                      border: `1px solid ${active ? PAL.layout.stroke : 'rgba(255,255,255,0.12)'}`,
                      background: active ? `${PAL.layout.stroke}20` : 'rgba(255,255,255,0.03)',
                      color: active ? PAL.layout.text : TXT.body,
                      transition: 'all 0.15s',
                    }}
                  >
                    {BG_FIT_ICONS[o.value]}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Incrustations */}
        <div style={sectionBoxCss}>
          <div style={sectionLabelCss}>Incrustations</div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              value={newIncrustType}
              onChange={e => setNewIncrustType(e.target.value as LayoutIncrustation['type'])}
              style={{ ...inputCss, flex: 1 }}
            >
              {INCRUST_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button
              type="button"
              onClick={addIncrust}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.4)',
                color: '#93c5fd', fontSize: 12, fontWeight: 600, flexShrink: 0,
              }}
            >
              <Plus style={{ width: 13, height: 13 }} /> Ajouter
            </button>
          </div>

          {slot.incrustations.length > 0 && (
            <>
              <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {slot.incrustations.map(ic => {
                  const col = INCRUST_COLORS[ic.type];
                  const sel = selectedIncrustId === ic.id;
                  return (
                    <div
                      key={ic.id}
                      onClick={() => setSelectedIncrustId(sel ? null : ic.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
                        background: sel ? col.fill : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${sel ? col.stroke : 'rgba(255,255,255,0.08)'}`,
                        transition: 'all 0.12s',
                      }}
                    >
                      <span style={{ fontSize: 12, color: col.text, flexShrink: 0 }}>
                        {ic.type === 'image' ? '🖼' : ic.type === 'video' ? '🎬' : '📹'}
                      </span>
                      <span style={{
                        flex: 1, fontSize: 12,
                        color: sel ? col.text : TXT.body,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {ic.label || ic.type}
                      </span>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); removeIncrust(ic.id); }}
                        style={{ background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer', fontSize: 14, padding: 0, flexShrink: 0, lineHeight: 1 }}
                      >×</button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {selectedIncrust && (
            <>
              <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, color: TXT.label, flexShrink: 0 }}>Nom :</span>
                  <input
                    type="text"
                    value={selectedIncrust.label}
                    placeholder="Nom…"
                    onChange={e => updIncrust(selectedIncrust.id, { label: e.target.value })}
                    style={{ ...inputCss, flex: 1, height: INPUT_H, boxSizing: 'border-box' }}
                  />
                </div>

                {selectedIncrust.type === 'source' ? (
                  <select
                    value={selectedIncrust.sourceId}
                    onChange={e => {
                      const incrustId    = selectedIncrust.id;
                      const sourceId     = e.target.value;
                      const sourceLabel  = availableSources.find(s => s.id === sourceId)?.label ?? '';

                      const track    = registry.get(sourceId)?.stream?.getVideoTracks()[0];
                      const settings = track?.getSettings();

                      if (settings?.width && settings?.height) {
                        const ratio = settings.width / settings.height;
                        setIncrustRatios(prev => ({ ...prev, [incrustId]: ratio }));
                        setIncrustNativeSizes(prev => ({
                          ...prev,
                          [incrustId]: { w: settings.width!, h: settings.height! },
                        }));
                        // Un téléphone (source 'remote') arrive en 1080p : à taille native il
                        // remplirait la scène. On le pose au tiers ; le natif reste atteignable
                        // via incrustNativeSizes.
                        const div = availableSources.find(s => s.id === sourceId)?.type === 'remote' ? 3 : 1;
                        updIncrust(incrustId, {
                          sourceId,
                          width : settings.width!  / REF_W * 100 / div,
                          height: settings.height! / REF_H * 100 / div,
                          label : sourceLabel,
                        });
                      } else {
                        updIncrust(incrustId, {
                          sourceId,
                          label: sourceLabel,
                        });
                      }
                    }}
                    style={inputCss}
                  >
                    <option value="">— Choisir une source —</option>
                    {availableSources.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                ) : (
                  // ── FIX v4.2 : capture de l'id stable avant l'await ──────────────
                  <div style={{ display: 'flex', gap: 6 }}>
                    <FilePickerButton
                      accept={selectedIncrust.type === 'image' ? 'image/*' : 'video/*'}
                      filename={selectedIncrust.originalName ?? incrustFileNames[selectedIncrust.id] ?? ''}
                      onFile={async (dataUrl, rawName) => {
                        // ← Capturer AVANT tout await pour éviter la stale closure
                        const incrustId    = selectedIncrust.id;
                        const incrustType  = selectedIncrust.type;
                        const instantMediaId = selectedIncrust.instantMediaId;

                        const { mediaId, objectUrl } = await storeMediaSource(
                          dataUrl, rawName, 'scene', slot.id,
                          `incrustation-${incrustId}`,
                          instantMediaId,
                        );

                        if (incrustType === 'image') {
                          const img = new window.Image();
                          img.onload = () => {
                            const ratio = img.naturalWidth / img.naturalHeight;
                            setIncrustRatios(prev => ({ ...prev, [incrustId]: ratio }));
                            setIncrustNativeSizes(prev => ({
                              ...prev,
                              [incrustId]: { w: img.naturalWidth, h: img.naturalHeight },
                            }));
                            // Taille native sans contrainte de cadre
                            const nativeWpct = img.naturalWidth  / REF_W * 100;
                            const nativeHpct = img.naturalHeight / REF_H * 100;
                            updIncrust(incrustId, {
                              url            : objectUrl,
                              instantMediaId : mediaId,
                              originalName   : rawName,
                              width          : nativeWpct,
                              height         : nativeHpct,
                              label          : stripExt(rawName),
                            });
                          };
                          img.src = objectUrl;
                        } else {
                          // Vidéo : taille native via loadedmetadata
                          const vid = document.createElement('video');
                          vid.preload = 'metadata';
                          vid.onloadedmetadata = () => {
                            const vw = vid.videoWidth;
                            const vh = vid.videoHeight;
                            if (vw && vh) {
                              const ratio = vw / vh;
                              setIncrustRatios(prev => ({ ...prev, [incrustId]: ratio }));
                              setIncrustNativeSizes(prev => ({
                                ...prev,
                                [incrustId]: { w: vw, h: vh },
                              }));
                              // Taille native sans contrainte de cadre
                              updIncrust(incrustId, {
                                url            : objectUrl,
                                instantMediaId : mediaId,
                                originalName   : rawName,
                                width          : vw / REF_W * 100,
                                height         : vh / REF_H * 100,
                                label          : stripExt(rawName),
                              });
                            } else {
                              // Dimensions indisponibles : on pose juste l'url
                              updIncrust(incrustId, {
                                url: objectUrl, instantMediaId: mediaId, originalName: rawName,
                                label: stripExt(rawName),
                              });
                            }
                          };
                          vid.onerror = () => {
                            updIncrust(incrustId, { url: objectUrl, instantMediaId: mediaId, originalName: rawName, label: stripExt(rawName) });
                          };
                          setVideoSource(vid, objectUrl);
                          vid.addEventListener('loadedmetadata', () => releaseVideoSource(vid), { once: true });
                        }

                        setIncrustFileNames(prev => ({ ...prev, [incrustId]: rawName }));
                      }}
                      onDelete={selectedIncrust.url ? async () => {
                        if (selectedIncrust.instantMediaId) deleteMediaRecord(selectedIncrust.instantMediaId);
                        updIncrust(selectedIncrust.id, { url: '', instantMediaId: '', originalName: '' });
                        setIncrustFileNames(prev => { const n = { ...prev }; delete n[selectedIncrust.id]; return n; });
                      } : undefined}
                    />
                  </div>
                )}

                <PositionSizeGrid
                  x={selectedIncrust.x}          y={selectedIncrust.y}
                  zIndex={selectedIncrust.zIndex} opacity={selectedIncrust.opacity}
                  width={selectedIncrust.width}   height={selectedIncrust.height}
                  minXPct={-100} minYPct={-100} maxXPct={200} maxYPct={200}
                  maxZ={slot.incrustations.length + 1}
                  locked={isLocked(selectedIncrust.id)}
                  onToggleLock={() => toggleLock(selectedIncrust.id)}
                  onX={p => updIncrust(selectedIncrust.id, { x: p })}
                  onY={p => updIncrust(selectedIncrust.id, { y: p })}
                  onZ={v => updIncrust(selectedIncrust.id, { zIndex: v })}
                  onOpacity={v => updIncrust(selectedIncrust.id, { opacity: v })}
                  onWidth={p  => updIncrustWidth(selectedIncrust.id, p)}
                  onHeight={p => updIncrustHeight(selectedIncrust.id, p)}
                  nativeSize={incrustNativeSizes[selectedIncrust.id] ?? null}
                  onNative={
                    incrustNativeSizes[selectedIncrust.id]
                      ? () => {
                          const { w: nw, h: nh } = incrustNativeSizes[selectedIncrust.id];
                          updIncrust(selectedIncrust.id, { width: nw / REF_W * 100, height: nh / REF_H * 100 });
                        }
                      : undefined
                  }
                  onAdapt={
                    incrustRatios[selectedIncrust.id] !== undefined
                      ? () => {
                          const ratio   = incrustRatios[selectedIncrust.id];
                          const slotWpx = selectedIncrust.width  / 100 * REF_W;
                          const slotHpx = selectedIncrust.height / 100 * REF_H;
                          if (slotWpx / slotHpx > ratio)
                            updIncrust(selectedIncrust.id, { width: (slotHpx * ratio) / REF_W * 100 });
                          else
                            updIncrust(selectedIncrust.id, { height: (slotWpx / ratio) / REF_H * 100 });
                        }
                      : undefined
                  }
                  onFullscreen={() => updIncrust(selectedIncrust.id, { x: 0, y: 0, width: 100, height: 100 })}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ══ Colonne droite — WYSIWYG ══ */}
      <div
        ref={rightColRef}
        style={{
          flex: 1, minWidth: 0,
          padding: '14px',
          display: 'flex', flexDirection: 'column', gap: 8,
          background: 'rgba(0,0,0,0.15)',
          overflowY: 'auto',
        }}
      >
        <div
          onClick={() => setSelectedIncrustId(null)}
          style={{
            width: wysiW, height: wysiH,
            position: 'relative',
            background: '#000',
            border: `2px solid ${PAL.layout.stroke}50`,
            borderRadius: 6,
            overflow: 'hidden',
            cursor: 'crosshair',
            flexShrink: 0,
          }}
        >
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.06,
            backgroundImage: [
              'linear-gradient(rgba(255,255,255,0.5) 1px,transparent 1px)',
              'linear-gradient(90deg,rgba(255,255,255,0.5) 1px,transparent 1px)',
            ].join(','),
            backgroundSize: `${wysiW / 8}px ${wysiH / 4.5}px`,
          }} />

          {renderBgPreview()}

          {slot.incrustations.map(ic => {
            const col = INCRUST_COLORS[ic.type];
            const sel = selectedIncrustId === ic.id;
            const left = ic.x      / 100 * wysiW;
            const top  = ic.y      / 100 * wysiH;
            const w    = ic.width  / 100 * wysiW;
            const h    = ic.height / 100 * wysiH;
            return (
              <div
                key={ic.id}
                onClick={e => { e.stopPropagation(); setSelectedIncrustId(ic.id); }}
                onMouseDown={e => handleWysiMouseDown(e, ic.id, 'move')}
                style={{
                  position: 'absolute', left, top, width: w, height: h,
                  background: sel ? col.fill : col.fill.replace('0.3', '0.15'),
                  border: `${sel ? 2 : 1}px solid ${sel ? col.text : col.stroke}`,
                  borderRadius: 3, cursor: 'move',
                  boxShadow: sel ? `0 0 8px ${col.stroke}60` : 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: ic.opacity, zIndex: ic.zIndex,
                  transition: dragRef.current ? 'none' : 'box-shadow 0.15s',
                }}
              >
                {ic.type === 'image' && ic.url && (
                  <img src={ic.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', inset: 0, pointerEvents: 'none' }} />
                )}
                {ic.type === 'video' && ic.url && (
                  <VideoThumb
                    url={ic.url}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', inset: 0, pointerEvents: 'none' as any }}
                  />
                )}
                {sel && RESIZE_CORNERS.map(k => (
                  <div
                    key={k.c}
                    onMouseDown={e => handleWysiMouseDown(e, ic.id, 'resize', k.c)}
                    style={{
                      position: 'absolute', ...k.pos,
                      width: 12, height: 12, cursor: k.cursor,
                      background: col.stroke, borderRadius: k.radius, opacity: 0.8,
                    }}
                  />
                ))}
              </div>
            );
          })}

          <div style={{ position: 'absolute', bottom: 2, right: 4, fontSize: 12, color: TXT.faint, fontFamily: 'monospace', pointerEvents: 'none' }}>
            {REF_W}×{REF_H}
          </div>
        </div>

        <HintBar>
          Cliquez un élément pour le sélectionner · Glissez pour déplacer · Coins pour redimensionner
        </HintBar>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Types du modal
// ══════════════════════════════════════════════════════════════════════════════

export type ConfigTab = 'images' | 'sounds' | 'jingles' | 'screen' | 'layouts';

interface MediaboardConfigModalProps {
  initialTab?: ConfigTab;
  initialLayoutIdx?: number;
  initialMediaIdx?: number;
  imageSlots: ImageSlot[];
  soundSlots: SoundSlot[];
  videoOverlaySlots: VideoOverlaySlot[];
  jingleSlots: JingleSlot[];
  remoteDevices: { device_id: string; label: string; is_connected: boolean }[];
  onImageChange: (slots: ImageSlot[]) => void;
  onSoundChange: (slots: SoundSlot[]) => void;
  onPipChange: (slots: VideoOverlaySlot[]) => void;
  onJingleChange: (slots: JingleSlot[]) => void;
  loadedAudioBuffers: Map<string, AudioBuffer>;
  onClose: () => void;
  screenShareSlots: ScreenShareSlot[];
  screenShareStreams: Map<string, MediaStream>;
  onScreenSlotsChange: (slots: ScreenShareSlot[]) => void;
  onScreenReset: (slotId: string) => Promise<void>;
  layoutSlots: LayoutSlot[];
  onLayoutChange: (slots: LayoutSlot[]) => void;
  availableSources: VideoSource[];
  /** Chaîne ouverte : nomme et range les sauvegardes de médias. */
  channelName: string;
  channelUuid: string;
}

// ══════════════════════════════════════════════════════════════════════════════
// Composant principal
// ══════════════════════════════════════════════════════════════════════════════

export const MediaboardConfigModal: React.FC<MediaboardConfigModalProps> = ({
  initialTab, initialLayoutIdx, initialMediaIdx,
  imageSlots, soundSlots, videoOverlaySlots, jingleSlots,
  remoteDevices,
  onImageChange, onSoundChange, onPipChange, onJingleChange,
  loadedAudioBuffers, onClose,
  screenShareSlots, screenShareStreams,
  onScreenSlotsChange, onScreenReset,
  layoutSlots, onLayoutChange, availableSources,
  channelName, channelUuid,
}) => {
  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const userName = authentication.admin ? `${authentication.admin.firstname} ${authentication.admin.lastname}` : 'Utilisateur';
  const [mediaConfigDialog, setMediaConfigDialog] = useState<'save' | 'load' | null>(null);
  // Cliché de l'état à l'ouverture — sert de retour arrière pour "Annuler".
  // useRef(x) ne capture x qu'au premier rendu : ce cliché reste figé même si
  // les props changent ensuite au fil des modifications de l'utilisateur.
  const initialSnapshotRef = useRef({
    imageSlots, soundSlots, videoOverlaySlots, jingleSlots, screenShareSlots, layoutSlots,
  });

  const handleCancel = useCallback(() => {
    const snap = initialSnapshotRef.current;
    onImageChange(snap.imageSlots);
    onSoundChange(snap.soundSlots);
    onPipChange(snap.videoOverlaySlots);
    onJingleChange(snap.jingleSlots);
    onScreenSlotsChange(snap.screenShareSlots);
    onLayoutChange(snap.layoutSlots);
    onClose();
  }, [onImageChange, onSoundChange, onPipChange, onJingleChange, onScreenSlotsChange, onLayoutChange, onClose]);

  const handleValidate = useCallback(() => {
    onClose();
  }, [onClose]);

  // Chargement d'une sauvegarde de médias : le contenu choisi REMPLACE celui des
  // familles concernées (les autres ne bougent pas). Les fichiers remplacés ne sont
  // PAS retirés d'IndexedDB : « Annuler » remet le cliché d'ouverture, dont les
  // slots pointent encore dessus — les effacer rouvrirait le bug de l'image
  // fantôme (cadre sans image).
  const handleLoadMedia = useCallback((payload: RegieConfigPayload) => {
    const { data } = payload;
    if (data.image) onImageChange(data.image.slots);
    if (data.son)   onSoundChange(data.son.slots);
    if (data.video) onJingleChange(data.video.slots);
    if (data.scene) onLayoutChange(data.scene.slots);
    if (data.pip) {
      const localCams = new Set(availableSources.filter(sv => sv.type === 'device').map(sv => sv.id));
      onPipChange(retargetPipDevices(data.pip.slots, localCams));
    }
  }, [onImageChange, onSoundChange, onJingleChange, onLayoutChange, onPipChange, availableSources]);

  const [tab, setTab] = useState<ConfigTab>(initialTab ?? 'images');
  const [highlightId, setHighlight] = useState<string | null>(null);
  const [fileNames, setFileNames] = useState<Record<string, string>>(() => loadFileNames());

  const setFile = useCallback((id: string, name: string) => {
    setFileNames(prev => {
      const next = { ...prev, [id]: name };
      saveFileNames(next);
      return next;
    });
  }, []);

  const [selectedImgIdx, setSelectedImgIdx] = useState(initialTab === 'images'  ? (initialMediaIdx ?? 0) : 0);
  const [selectedJglIdx, setSelectedJglIdx] = useState(initialTab === 'jingles' ? (initialMediaIdx ?? 0) : 0);
  const [selectedSndIdx, setSelectedSndIdx] = useState(initialTab === 'sounds'  ? (initialMediaIdx ?? 0) : 0);
  const [selectedScreenIdx, setSelectedScreenIdx] = useState(initialTab === 'screen' ? (initialMediaIdx ?? 0) : 0);

  // ── Ratios / cadenas images ─────────────────────────────────────────────────
  const [imageRatios, setImageRatios] = useState<Record<string, number>>({});
  const [imageNativeSizes, setImageNativeSizes] = useState<Record<string, { w: number; h: number }>>({});
  const [imageLockedRatios, setImageLockedRatios] = useState<Record<string, boolean>>({});

  const isImageLocked = useCallback((id: string) => imageLockedRatios[id] !== false, [imageLockedRatios]);
  const toggleImageLock = useCallback((id: string) => setImageLockedRatios(prev => ({ ...prev, [id]: !isImageLocked(id) })), [isImageLocked]);

  // ── Ratios / cadenas jingles ────────────────────────────────────────────────
  const [jingleRatios, setJingleRatios] = useState<Record<string, number>>({});
  const [jingleNativeSizes, setJingleNativeSizes] = useState<Record<string, { w: number; h: number }>>({});
  const [jingleLockedRatios, setJingleLockedRatios] = useState<Record<string, boolean>>({});

  const isJingleLocked = useCallback((id: string) => jingleLockedRatios[id] !== false, [jingleLockedRatios]);
  const toggleJingleLock = useCallback((id: string) => setJingleLockedRatios(prev => ({ ...prev, [id]: !isJingleLocked(id) })), [isJingleLocked]);

  const [jingleMuted, setJingleMuted] = useState<Record<string, boolean>>({});

  // ── Son ────────────────────────────────────────────────────────────────────
  // Les bornes de lecture (trimStart/trimEnd) sont persistées directement sur le
  // SoundSlot (secondes) afin d'être appliquées lors du déclenchement depuis le deck.
  const [soundDuration, setSoundDuration] = useState<Record<string, number>>({});
  const [, setSoundStereo]     = useState<Record<string, boolean>>({});

  // Confirmation par le composant Window de l'application (comme la suppression d'une
  // chaîne), et non par window.confirm : la boîte système sort du style de l'interface.
  const [showResetAllConfirm, setShowResetAllConfirm] = useState(false);

  const performResetAll = useCallback(() => {
    clearSlotsFromIndexedDB(imageSlots);
    clearSlotsFromIndexedDB(soundSlots);
    clearSlotsFromIndexedDB(jingleSlots);
    clearSceneSlotsFromIndexedDB(layoutSlots);

    onImageChange(DEFAULT_IMAGE_SLOTS.map(s => ({ ...s })));
    onSoundChange(DEFAULT_SOUND_SLOTS.map(s => ({ ...s })));
    onPipChange(DEFAULT_VIDEO_OVERLAY_SLOTS.map(s => ({ ...s })));
    onJingleChange(DEFAULT_JINGLE_SLOTS.map(s => ({ ...s })));
    onScreenSlotsChange(DEFAULT_SCREEN_SHARE_SLOTS.map(s => ({ ...s, config: { ...s.config } })));
    onLayoutChange(DEFAULT_LAYOUT_SLOTS.map(s => ({ ...s, background: { ...s.background }, incrustations: [] })));

    setFileNames({});
    saveFileNames({});
    setImageRatios({});
    setImageNativeSizes({});
    setImageLockedRatios({});
    setJingleRatios({});
    setJingleNativeSizes({});
    setJingleLockedRatios({});
    setJingleMuted({});
    setSoundDuration({});
    setSoundStereo({});
    setSelectedImgIdx(0);
    setSelectedJglIdx(0);
    setSelectedSndIdx(0);
    setSelectedScreenIdx(0);
  }, [
    imageSlots, soundSlots, jingleSlots, layoutSlots,
    onImageChange, onSoundChange, onPipChange, onJingleChange, onScreenSlotsChange, onLayoutChange,
  ]);

  const handleResetAll = useCallback(() => { setShowResetAllConfirm(true); }, []);

  const getSoundDur = (id: string) => soundDuration[id] ?? 0;
  const getSoundStart = (id: string) => {
    const dur = getSoundDur(id);
    const s = soundSlots.find(sl => sl.id === id);
    if (!s || dur <= 0) return 0;
    return Math.max(0, Math.min(100, ((s.trimStart ?? 0) / dur) * 100));
  };
  const getSoundEnd = (id: string) => {
    const dur = getSoundDur(id);
    const s = soundSlots.find(sl => sl.id === id);
    if (!s || dur <= 0) return 100;
    return Math.max(0, Math.min(100, ((s.trimEnd ?? dur) / dur) * 100));
  };

  const loadAudioMeta = useCallback((id: string, url: string) => {
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      setSoundDuration(prev => ({ ...prev, [id]: audio.duration }));
    });
    try {
      const ctx = new AudioContext();
      fetch(url).then(r => r.arrayBuffer()).then(buf => ctx.decodeAudioData(buf)).then(decoded => {
        setSoundStereo(prev => ({ ...prev, [id]: decoded.numberOfChannels >= 2 }));
        ctx.close();
      }).catch(() => ctx.close());
    } catch {}
  }, []);

  // Chargement ratios images existantes au montage
  useEffect(() => {
    imageSlots.forEach(slot => {
      if (!slot.url || imageRatios[slot.id] !== undefined) return;
      const img = new window.Image();
      img.onload = () => {
        const ratio = img.naturalWidth / img.naturalHeight;
        setImageRatios(prev => prev[slot.id] !== undefined ? prev : { ...prev, [slot.id]: ratio });
        setImageNativeSizes(prev => prev[slot.id] !== undefined ? prev : { ...prev, [slot.id]: { w: img.naturalWidth, h: img.naturalHeight } });
      };
      img.src = slot.url;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSlots]);

  // Audio preview
  const [, setPreviewingId] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioSrcRef = useRef<AudioBufferSourceNode | null>(null);

  const stopPreview = useCallback(() => {
    try { audioSrcRef.current?.stop(); } catch {}
    audioSrcRef.current = null;
    try { audioCtxRef.current?.close(); } catch {}
    audioCtxRef.current = null;
    setPreviewingId(null); setPreviewError(null);
  }, []);

  useEffect(() => () => stopPreview(), [stopPreview]);

  // Helpers
  const updImg = (i: number, p: Partial<ImageSlot>) =>
    onImageChange(imageSlots.map((s, j) => j === i ? { ...s, ...p } : s));

  const updImgWidth = useCallback((idx: number, slot: ImageSlot, newWpct: number) => {
    const ratio = imageRatios[slot.id];
    if (isImageLocked(slot.id) && ratio) {
      onImageChange(imageSlots.map((s, j) => j === idx ? { ...s, width: newWpct, height: Math.max(0.5, (newWpct * REF_W) / (ratio * REF_H)) } : s));
    } else {
      onImageChange(imageSlots.map((s, j) => j === idx ? { ...s, width: newWpct } : s));
    }
  }, [imageRatios, isImageLocked, imageSlots, onImageChange]);

  const updImgHeight = useCallback((idx: number, slot: ImageSlot, newHpct: number) => {
    const ratio = imageRatios[slot.id];
    if (isImageLocked(slot.id) && ratio) {
      onImageChange(imageSlots.map((s, j) => j === idx ? { ...s, height: newHpct, width: Math.max(0.5, (newHpct * ratio * REF_H) / REF_W) } : s));
    } else {
      onImageChange(imageSlots.map((s, j) => j === idx ? { ...s, height: newHpct } : s));
    }
  }, [imageRatios, isImageLocked, imageSlots, onImageChange]);

  const updJgl = (i: number, p: Partial<JingleSlot>) =>
    onJingleChange(jingleSlots.map((s, j) => j === i ? { ...s, ...p } : s));

  const updJglWidth = useCallback((idx: number, slot: JingleSlot, newWpct: number) => {
    const ratio = jingleRatios[slot.id];
    if (isJingleLocked(slot.id) && ratio) {
      onJingleChange(jingleSlots.map((s, j) => j === idx ? { ...s, width: newWpct, height: Math.max(0.5, (newWpct * REF_W) / (ratio * REF_H)) } : s));
    } else {
      onJingleChange(jingleSlots.map((s, j) => j === idx ? { ...s, width: newWpct } : s));
    }
  }, [jingleRatios, isJingleLocked, jingleSlots, onJingleChange]);

  const updJglHeight = useCallback((idx: number, slot: JingleSlot, newHpct: number) => {
    const ratio = jingleRatios[slot.id];
    if (isJingleLocked(slot.id) && ratio) {
      onJingleChange(jingleSlots.map((s, j) => j === idx ? { ...s, height: newHpct, width: Math.max(0.5, (newHpct * ratio * REF_H) / REF_W) } : s));
    } else {
      onJingleChange(jingleSlots.map((s, j) => j === idx ? { ...s, height: newHpct } : s));
    }
  }, [jingleRatios, isJingleLocked, jingleSlots, onJingleChange]);

  const updSnd = (i: number, p: Partial<SoundSlot>) =>
    onSoundChange(soundSlots.map((s, j) => j === i ? { ...s, ...p } : s));

  // Onglets
  const tabCss = (id: ConfigTab): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', border: 'none', transition: 'all 0.15s',
    display: 'flex', alignItems: 'center', gap: 7,
    background: tab === id ? '#6366f1' : 'transparent',
    color: tab === id ? '#ffffff' : TXT.label,
    boxShadow: tab === id ? '0 2px 10px rgba(99,102,241,0.35)' : 'none',
  });

  const isScreenStreamActive = (stream: MediaStream | undefined) =>
    !!(stream?.active && stream.getVideoTracks()[0]?.readyState === 'live');
  const anyScreenActive = screenShareSlots.some(slot => isScreenStreamActive(screenShareStreams.get(slot.id)));
  const hasScenes = layoutSlots.some(s => s.background.type !== 'none' || s.incrustations.length > 0);

  const curImg = imageSlots[selectedImgIdx];
  const curJgl = jingleSlots[selectedJglIdx];
  const curSnd = soundSlots[selectedSndIdx];

  // ── Render colonne gauche images ────────────────────────────────────────────

  const renderImageLeft = () => {
    if (!curImg) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', flex: 1 }}>
        <ThumbnailGrid
          slots={imageSlots}
          selectedIdx={selectedImgIdx}
          accentColor={PAL.image.stroke}
          onSelect={setSelectedImgIdx}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: TXT.label, flexShrink: 0 }}>Nom :</span>
          <input
            type="text"
            value={curImg.label}
            placeholder={(curImg.originalName ?? fileNames[curImg.id]) ? stripExt((curImg.originalName ?? fileNames[curImg.id])) : `Image ${selectedImgIdx + 1}`}
            onChange={e => updImg(selectedImgIdx, { label: e.target.value })}
            style={{ ...inputCss, flex: 1, height: INPUT_H, boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          <FilePickerButton
            accept="image/*"
            filename={(curImg.originalName ?? fileNames[curImg.id]) ?? ''}
            onFile={async (dataUrl, rawName) => {
              const baseName = stripExt(rawName);
              setFile(curImg.id, rawName);

              const { mediaId, objectUrl } = await storeMediaSource(
                dataUrl, rawName, 'image', curImg.id, 'slot', curImg.instantMediaId,
              );

              const img = new window.Image();
              img.onload = () => {
                const ratio = img.naturalWidth / img.naturalHeight;
                setImageRatios(prev => ({ ...prev, [curImg.id]: ratio }));
                setImageNativeSizes(prev => ({ ...prev, [curImg.id]: { w: img.naturalWidth, h: img.naturalHeight } }));
                // Taille native sans contrainte
                const nativeWpct = img.naturalWidth  / REF_W * 100;
                const nativeHpct = img.naturalHeight / REF_H * 100;
                updImg(selectedImgIdx, {
                  url            : objectUrl,
                  instantMediaId : mediaId,
                  originalName   : rawName,
                  width          : nativeWpct,
                  height         : nativeHpct,
                  label          : baseName,
                });
              };
              img.src = objectUrl;
            }}
            onDelete={async () => {
            if (curImg.instantMediaId) deleteMediaRecord(curImg.instantMediaId);
            setFile(curImg.id, '');
            setImageRatios(prev => { const n = { ...prev }; delete n[curImg.id]; return n; });
            setImageNativeSizes(prev => { const n = { ...prev }; delete n[curImg.id]; return n; });
            updImg(selectedImgIdx, { url: '', label: '', instantMediaId: '', originalName: '' });
            }}
          />
        </div>

        <PositionSizeGrid
          x={curImg.x} y={curImg.y}
          zIndex={(curImg as any).zIndex ?? 0}
          opacity={curImg.opacity}
          width={curImg.width} height={curImg.height}
          locked={isImageLocked(curImg.id)}
          onToggleLock={() => toggleImageLock(curImg.id)}
          onX={p => updImg(selectedImgIdx, { x: p })}
          onY={p => updImg(selectedImgIdx, { y: p })}
          onZ={v => updImg(selectedImgIdx, { zIndex: v } as any)}
          onOpacity={v => updImg(selectedImgIdx, { opacity: v })}
          onWidth={p => updImgWidth(selectedImgIdx, curImg, p)}
          onHeight={p => updImgHeight(selectedImgIdx, curImg, p)}
          nativeSize={imageNativeSizes[curImg.id] ?? null}
          onNative={imageNativeSizes[curImg.id] ? () => {
            const { w: nw, h: nh } = imageNativeSizes[curImg.id];
            updImg(selectedImgIdx, { width: nw / REF_W * 100, height: nh / REF_H * 100 });
          } : undefined}
          onAdapt={imageRatios[curImg.id] !== undefined ? () => {
            const ratio = imageRatios[curImg.id];
            const slotWpx = curImg.width  / 100 * REF_W;
            const slotHpx = curImg.height / 100 * REF_H;
            if (slotWpx / slotHpx > ratio)
              updImg(selectedImgIdx, { width: (slotHpx * ratio) / REF_W * 100 });
            else
              updImg(selectedImgIdx, { height: (slotWpx / ratio) / REF_H * 100 });
          } : undefined}
          onFullscreen={() => updImg(selectedImgIdx, { x: 0, y: 0, width: 100, height: 100 })}
        />

        <RawPreview url={curImg.url} type="image" />
      </div>
    );
  };

  // ── Render colonne gauche jingles ───────────────────────────────────────────

  const renderJingleLeft = () => {
    if (!curJgl) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', flex: 1 }}>
        <ThumbnailGrid
          slots={jingleSlots}
          selectedIdx={selectedJglIdx}
          accentColor={PAL.jingle.stroke}
          onSelect={setSelectedJglIdx}
          mediaType="video"
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: TXT.label, flexShrink: 0 }}>Nom :</span>
          <input
            type="text"
            value={curJgl.label}
            placeholder={(curJgl.originalName ?? fileNames[curJgl.id]) ? stripExt((curJgl.originalName ?? fileNames[curJgl.id])) : `Vidéo ${selectedJglIdx + 1}`}
            onChange={e => updJgl(selectedJglIdx, { label: e.target.value })}
            style={{ ...inputCss, flex: 1, height: INPUT_H, boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          <FilePickerButton
            accept="video/*"
            filename={(curJgl.originalName ?? fileNames[curJgl.id]) ?? ''}
            onFile={async (dataUrl, rawName) => {
              const baseName = stripExt(rawName);
              setFile(curJgl.id, rawName);

              const { mediaId, objectUrl } = await storeMediaSource(
                dataUrl, rawName, 'video', curJgl.id, 'slot', curJgl.instantMediaId,
              );

              const vid = document.createElement('video');
              vid.preload = 'metadata';
              vid.onloadedmetadata = () => {
                const w = vid.videoWidth;
                const h = vid.videoHeight;
                const extraProps: any = {};
                if (w && h) {
                  const ratio = w / h;
                  setJingleRatios(prev => ({ ...prev, [curJgl.id]: ratio }));
                  setJingleNativeSizes(prev => ({ ...prev, [curJgl.id]: { w, h } }));
                  // Taille native sans contrainte
                  extraProps.width  = w / REF_W * 100;
                  extraProps.height = h / REF_H * 100;
                }
                updJgl(selectedJglIdx, {
                  url            : objectUrl,
                  instantMediaId : mediaId,
                  originalName   : rawName,
                  ...extraProps,
                  label          : baseName,
                } as any);
              };
              vid.onerror = () => {
                updJgl(selectedJglIdx, {
                  url            : objectUrl,
                  instantMediaId : mediaId,
                  originalName   : rawName,
                  label          : baseName,
                });
              };
              setVideoSource(vid, objectUrl);
              vid.addEventListener('loadedmetadata', () => releaseVideoSource(vid), { once: true });
            }}
            onDelete={async () => {
            if (curJgl.instantMediaId) deleteMediaRecord(curJgl.instantMediaId);
            setFile(curJgl.id, '');
            setJingleRatios(prev => { const n = { ...prev }; delete n[curJgl.id]; return n; });
            setJingleNativeSizes(prev => { const n = { ...prev }; delete n[curJgl.id]; return n; });
            updJgl(selectedJglIdx, { url: '', label: '', instantMediaId: '', originalName: '' } as any);
            }}
          />
        </div>

        <PositionSizeGrid
          x={(curJgl as any).x ?? 0} y={(curJgl as any).y ?? 0}
          zIndex={(curJgl as any).zIndex ?? 0}
          opacity={(curJgl as any).opacity ?? 1}
          width={(curJgl as any).width ?? 100}
          height={(curJgl as any).height ?? 100}
          locked={isJingleLocked(curJgl.id)}
          onToggleLock={() => toggleJingleLock(curJgl.id)}
          onX={p => updJgl(selectedJglIdx, { x: p } as any)}
          onY={p => updJgl(selectedJglIdx, { y: p } as any)}
          onZ={v => updJgl(selectedJglIdx, { zIndex: v } as any)}
          onOpacity={v => updJgl(selectedJglIdx, { opacity: v } as any)}
          onWidth={p => updJglWidth(selectedJglIdx, curJgl, p)}
          onHeight={p => updJglHeight(selectedJglIdx, curJgl, p)}
          nativeSize={jingleNativeSizes[curJgl.id] ?? null}
          onNative={jingleNativeSizes[curJgl.id] ? () => {
            const { w: nw, h: nh } = jingleNativeSizes[curJgl.id];
            updJgl(selectedJglIdx, { width: nw / REF_W * 100, height: nh / REF_H * 100 } as any);
          } : undefined}
          onAdapt={jingleRatios[curJgl.id] !== undefined ? () => {
            const ratio = jingleRatios[curJgl.id];
            const slotWpx = ((curJgl as any).width ?? 100) / 100 * REF_W;
            const slotHpx = ((curJgl as any).height ?? 100) / 100 * REF_H;
            if (slotWpx / slotHpx > ratio) {
              updJgl(selectedJglIdx, { width: (slotHpx * ratio) / REF_W * 100 } as any);
            } else {
              updJgl(selectedJglIdx, { height: (slotWpx / ratio) / REF_H * 100 } as any);
            }
          } : undefined}
          onFullscreen={() => updJgl(selectedJglIdx, { x: 0, y: 0, width: 100, height: 100 } as any)}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: TXT.label, flexShrink: 0 }}>Vol.</span>
          <input
            type="range" min={0} max={1} step={0.05} value={curJgl.volume}
            onChange={e => updJgl(selectedJglIdx, { volume: +e.target.value })}
            style={{ flex: 1, minWidth: 40, accentColor: PAL.jingle.stroke }}
          />
          <span style={{ fontSize: 12, color: '#e2e8f0', minWidth: 28, textAlign: 'right', flexShrink: 0 }}>
            {Math.round(curJgl.volume * 100)}%
          </span>
          <MuteButton
            muted={jingleMuted[curJgl.id] ?? false}
            onToggle={() => setJingleMuted(prev => ({ ...prev, [curJgl.id]: !prev[curJgl.id] }))}
            accentColor={PAL.jingle.stroke}
          />
          <AudioMixerToggle
            active={!!(curJgl as any).audioToMixer}
            onToggle={() => updJgl(selectedJglIdx, { audioToMixer: !(curJgl as any).audioToMixer } as any)}
            accentColor={PAL.jingle.stroke}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', flexShrink: 0 }}>
            <input
              type="checkbox"
              checked={!!(curJgl as any).loop}
              onChange={e => updJgl(selectedJglIdx, { loop: e.target.checked } as any)}
              style={{ width: 13, height: 13, accentColor: PAL.jingle.stroke }}
            />
            <span style={{ fontSize: 12, color: TXT.body, whiteSpace: 'nowrap' }}>Boucle</span>
          </label>
        </div>

        <JingleVideoPlayer url={curJgl.url} accentColor={PAL.jingle.stroke} />
      </div>
    );
  };

  // ── Render sons ─────────────────────────────────────────────────────────────

  const renderSounds = () => {
    if (!curSnd) return null;

    const startPct = getSoundStart(curSnd.id);
    const endPct   = getSoundEnd(curSnd.id);
    const dur      = getSoundDur(curSnd.id);

    return (
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

        {/* ── Colonne gauche ── */}
        <div style={{
          width: 350, flexShrink: 0,
          borderRight: '1px solid rgba(255,255,255,0.06)',
          padding: '14px 14px',
          display: 'flex', flexDirection: 'column', gap: 10,
          overflowY: 'auto',
        }}>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
            {soundSlots.map((s, idx) => {
              const selected = idx === selectedSndIdx;
              return (
                <div
                  key={s.id}
                  onClick={() => setSelectedSndIdx(idx)}
                  title={s.label || (fileNames[s.id] ? stripExt(fileNames[s.id]) : `Son ${idx + 1}`)}
                  style={{
                    aspectRatio: '16/9',
                    borderRadius: 4,
                    border: `${selected ? 2 : 1}px solid ${selected ? PAL.sound.stroke : 'rgba(255,255,255,0.1)'}`,
                    background: selected ? PAL.sound.stroke + '15' : 'rgba(255,255,255,0.04)',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'border-color 0.12s, background 0.12s',
                    boxShadow: selected ? `0 0 6px ${PAL.sound.stroke}40` : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexDirection: 'column', gap: 2,
                  }}
                >
                  {s.url
                    ? <span style={{ fontSize: 14 }}>🎵</span>
                    : <span style={{ fontSize: 12, color: TXT.faint, fontStyle: 'italic' }}>vide</span>
                  }
                  {(s.label || fileNames[s.id]) && (
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      fontSize: 12,
                      color: selected ? PAL.sound.stroke : TXT.help,
                      background: 'rgba(0,0,0,0.6)',
                      padding: '1px 3px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      textAlign: 'center',
                    }}>
                      {s.label || stripExt(fileNames[s.id])}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: TXT.label, flexShrink: 0 }}>Nom :</span>
            <input
              type="text"
              value={curSnd.label}
              placeholder={(curSnd.originalName ?? fileNames[curSnd.id]) ? stripExt((curSnd.originalName ?? fileNames[curSnd.id])) : `Son ${selectedSndIdx + 1}`}
              onChange={e => updSnd(selectedSndIdx, { label: e.target.value })}
              style={{ ...inputCss, flex: 1, height: INPUT_H, boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            <FilePickerButton
              accept="audio/*"
              filename={(curSnd.originalName ?? fileNames[curSnd.id]) ?? ''}
              onFile={async (dataUrl, rawName) => {
                const baseName = stripExt(rawName);
                setFile(curSnd.id, rawName);

                const { mediaId, objectUrl } = await storeMediaSource(
                  dataUrl, rawName, 'sound', curSnd.id, 'slot', curSnd.instantMediaId,
                );

                updSnd(selectedSndIdx, {
                  url            : objectUrl,
                  instantMediaId : mediaId,
                  originalName   : rawName,
                  trimStart      : 0,
                  trimEnd        : undefined,
                  label          : baseName,
                });
                loadAudioMeta(curSnd.id, objectUrl);
              }}
              onDelete={async () => {
              if (curSnd.instantMediaId) deleteMediaRecord(curSnd.instantMediaId);
              setFile(curSnd.id, '');
              updSnd(selectedSndIdx, { url: '', label: '', instantMediaId: '', originalName: '', trimStart: 0, trimEnd: undefined });
              setSoundDuration(prev => { const n = { ...prev }; delete n[curSnd.id]; return n; });
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: TXT.label, flexShrink: 0 }}>Vol.</span>
            <input
              type="range" min={0} max={1} step={0.05} value={curSnd.volume}
              onChange={e => updSnd(selectedSndIdx, { volume: +e.target.value })}
              style={{ flex: 1, minWidth: 40, accentColor: PAL.sound.stroke }}
            />
            <span style={{ fontSize: 12, color: '#e2e8f0', minWidth: 28, textAlign: 'right', flexShrink: 0 }}>
              {Math.round(curSnd.volume * 100)}%
            </span>
            <MuteButton
              muted={!!(curSnd as any).muted}
              onToggle={() => updSnd(selectedSndIdx, { muted: !(curSnd as any).muted } as any)}
              accentColor={PAL.sound.stroke}
            />
            <AudioMixerToggle
              active={!!(curSnd as any).audioToMixer}
              onToggle={() => updSnd(selectedSndIdx, { audioToMixer: !(curSnd as any).audioToMixer } as any)}
              accentColor={PAL.sound.stroke}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', flexShrink: 0 }}>
              <input
                type="checkbox"
                checked={curSnd.loop}
                onChange={e => updSnd(selectedSndIdx, { loop: e.target.checked })}
                style={{ width: 13, height: 13, accentColor: PAL.sound.stroke }}
              />
              <span style={{ fontSize: 12, color: TXT.body, whiteSpace: 'nowrap' }}>Boucle</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <TimecodeInput
              label="Début"
              sec={startPct / 100 * dur}
              duration={dur}
              accentColor={PAL.sound.text}
              onChange={v => updSnd(selectedSndIdx, { trimStart: v / 100 * dur })}
              minPct={0}
              maxPct={endPct - 1}
            />
            <TimecodeInput
              label="Fin"
              sec={endPct / 100 * dur}
              duration={dur}
              accentColor={PAL.sound.text}
              onChange={v => updSnd(selectedSndIdx, { trimEnd: v / 100 * dur })}
              minPct={startPct + 1}
              maxPct={100}
            />
          </div>

        </div>

        {/* ── Colonne droite — grande forme d'onde ── */}
        <div style={{
          flex: 1, minWidth: 0,
          padding: '14px',
          display: 'flex', flexDirection: 'column', gap: 8,
          background: 'rgba(0,0,0,0.15)',
          overflowY: 'auto',
        }}>
          <div style={sectionLabelCss}>Forme d'onde</div>
          <SoundWaveform
            url={curSnd.url}
            accentColor={PAL.sound.stroke}
            startPct={startPct}
            endPct={endPct}
            duration={dur}
            onStartChange={v => updSnd(selectedSndIdx, { trimStart: v / 100 * dur })}
            onEndChange={v => updSnd(selectedSndIdx, { trimEnd: v / 100 * dur })}
            onDurationChange={sec => setSoundDuration(prev => ({ ...prev, [curSnd.id]: sec }))}
          />
          <HintBar>
            Glissez les poignées pour définir le début et la fin de lecture
          </HintBar>
        </div>

      </div>
    );
  };

  // Canvas items
  const imageCanvasItems: CanvasItem[] = imageSlots.map(s => ({
    id: s.id, url: s.url, label: s.label || (fileNames[s.id] ? stripExt(fileNames[s.id]) : s.id),
    x: s.x, y: s.y, width: s.width, height: s.height,
    opacity: s.opacity, zIndex: (s as any).zIndex ?? 0,
    accentColor: PAL.image.stroke,
  }));

  const jingleCanvasItems: CanvasItem[] = jingleSlots.map(s => ({
    id: s.id, url: (s as any).url ?? '',
    label: s.label || (fileNames[s.id] ? stripExt(fileNames[s.id]) : s.id),
    x: (s as any).x ?? 0, y: (s as any).y ?? 0,
    width: (s as any).width ?? 100, height: (s as any).height ?? 100,
    opacity: (s as any).opacity ?? 1, zIndex: (s as any).zIndex ?? 0,
    accentColor: PAL.jingle.stroke,
    mediaType: 'video' as const,
  }));

  const handleImageItemChange = (id: string, patch: Partial<CanvasItem>) =>
    onImageChange(imageSlots.map(s => s.id === id ? { ...s, ...patch } : s));

  const handleJingleItemChange = (id: string, patch: Partial<CanvasItem>) =>
    onJingleChange(jingleSlots.map(s => s.id === id ? { ...s, ...patch } : s));

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      {showResetAllConfirm && (
        <Window message                 = {<>Tout réinitialiser ? Cette action supprime toutes les images, sons, vidéos, scènes et copies d'écran configurés dans le mediaboard.</>}
                messageType             = "question"
                onClickOnValidateButton = {() => { setShowResetAllConfirm(false); performResetAll(); }}
                onClickOnCancelButton   = {() => setShowResetAllConfirm(false)}
                validButtonText         = "Oui"
                cancelButtonText        = "Non" />
      )}
      <style>{`
        .mbc-root .mbc-tab[aria-selected="false"]:hover { background: rgba(255,255,255,0.07) !important; color: #f1f5f9 !important; }
        .mbc-root input[type="text"]:focus, .mbc-root input[type="number"]:focus, .mbc-root select:focus {
          border-color: #818cf8 !important; box-shadow: 0 0 0 2px rgba(99,102,241,0.28);
        }
        .mbc-root input::placeholder { color: ${TXT.faint}; }
      `}</style>
      <div className="mbc-root" style={{
        background: 'var(--color-Panel-niv1-BG)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        width: '100%', maxWidth: 1360,
        height: '88vh',
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 32px 80px rgba(0,0,0,0.85)',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0,
        }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#ffffff', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(99,102,241,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Settings size={16} color="#818cf8" />
            </span>
            Médias et scènes
            {channelName && <span style={{ fontSize: 13, fontWeight: 500, color: TXT.help }}>· {channelName}</span>}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {(['save', 'load'] as const).map(mode => (
              <button
                key={mode} type="button" disabled={!channelUuid}
                onClick={() => setMediaConfigDialog(mode)}
                title={mode === 'save' ? 'Enregistrer images, sons, vidéos et scènes de cette chaîne' : 'Recharger une sauvegarde de médias'}
                style={{ ...HEAD_BTN, cursor: channelUuid ? 'pointer' : 'not-allowed', opacity: channelUuid ? 1 : 0.4,
                         border: '1px solid rgba(129,140,248,0.45)', background: 'rgba(99,102,241,0.14)', color: '#c7d2fe' }}
              >
                {mode === 'save' ? <Save size={14} /> : <FolderOpen size={14} />}
                {mode === 'save' ? 'Sauvegarder' : 'Charger'}
              </button>
            ))}
            <span style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
            <button
              type="button" onClick={handleResetAll}
              title="Tout réinitialiser"
              style={{ ...HEAD_BTN, cursor: 'pointer', border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.08)', color: '#fca5a5' }}
            >
              <Trash2 size={14} /> Tout réinitialiser
            </button>
          </div>
        </div>

        {/* Onglets — contrôle segmenté ; Scènes et Écran gardent leur couleur et leur
            pastille « en service ». */}
        <div style={{ padding: '10px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
        <div role="tablist" style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: 11, background: 'rgba(0,0,0,0.28)', border: '1px solid rgba(255,255,255,0.08)', flexWrap: 'wrap' }}>
          <button type="button" role="tab" aria-selected={tab === 'images'}  className="mbc-tab" style={tabCss('images')}  onClick={() => setTab('images')}><ImageIcon size={15} /> Images</button>
          <button type="button" role="tab" aria-selected={tab === 'jingles'} className="mbc-tab" style={tabCss('jingles')} onClick={() => setTab('jingles')}><Film size={15} /> Vidéos</button>
          <button type="button" role="tab" aria-selected={tab === 'sounds'}  className="mbc-tab" style={tabCss('sounds')}  onClick={() => setTab('sounds')}><Music size={15} /> Sons</button>
          <button
            type="button" role="tab" aria-selected={tab === 'layouts'} className="mbc-tab"
            style={{
              ...tabCss('layouts'),
              background: tab === 'layouts' ? PAL.layout.stroke : hasScenes ? `${PAL.layout.stroke}1f` : 'transparent',
              color: tab === 'layouts' ? '#0b1222' : hasScenes ? PAL.layout.text : TXT.label,
              boxShadow: tab === 'layouts' ? `0 2px 10px ${PAL.layout.stroke}55` : 'none',
            }}
            onClick={() => setTab('layouts')}
          >
            <Layout size={15} /> Scènes
            {hasScenes && <span title="Au moins une scène configurée" style={{ width: 7, height: 7, borderRadius: '50%', background: tab === 'layouts' ? '#0b1222' : PAL.layout.stroke, display: 'inline-block' }} />}
          </button>
          <button
            type="button" role="tab" aria-selected={tab === 'screen'} className="mbc-tab"
            style={{
              ...tabCss('screen'),
              background: tab === 'screen' ? PAL.screen.stroke : anyScreenActive ? `${PAL.screen.stroke}1f` : 'transparent',
              color: tab === 'screen' ? '#0b1222' : anyScreenActive ? PAL.screen.text : TXT.label,
              boxShadow: tab === 'screen' ? `0 2px 10px ${PAL.screen.stroke}55` : 'none',
            }}
            onClick={() => setTab('screen')}
          >
            <Monitor size={15} /> Écran
            {anyScreenActive && <span title="Une copie d'écran est en cours" style={{ width: 7, height: 7, borderRadius: '50%', background: tab === 'screen' ? '#0b1222' : PAL.screen.stroke, display: 'inline-block' }} />}
          </button>
        </div>
        </div>

        {/* Erreur audio */}
        {previewError && (
          <div style={{
            margin: '8px 18px 0', padding: '7px 12px',
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 8, fontSize: 12, color: '#fca5a5',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
          }}>
            {previewError}
            <button type="button" onClick={() => setPreviewError(null)} style={{ background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer', fontSize: 16 }}>×</button>
          </div>
        )}

        {/* Corps principal */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

          {tab === 'screen' && (() => {
            const curScreen = screenShareSlots[selectedScreenIdx] ?? screenShareSlots[0];
            const curScreenStream = curScreen ? screenShareStreams.get(curScreen.id) ?? null : null;
            const curScreenActive = isScreenStreamActive(curScreenStream ?? undefined);
            return (
              <>
                <div style={{
                  width: 350, flexShrink: 0,
                  borderRight: '1px solid rgba(255,255,255,0.06)',
                  padding: '14px 14px',
                  display: 'flex', flexDirection: 'column', gap: 12,
                  overflowY: 'auto',
                }}>
                  <ScreenSlotSelector
                    slots={screenShareSlots}
                    streams={screenShareStreams}
                    selectedIdx={selectedScreenIdx}
                    accentColor={PAL.screen.stroke}
                    onSelect={setSelectedScreenIdx}
                  />
                  {curScreen && (
                    <ScreenShareConfigPanel
                      label={curScreen.label}
                      placeholder={`Copie d'écran ${selectedScreenIdx + 1}`}
                      config={curScreen.config}
                      streamActive={curScreenActive}
                      screenShareStream={curScreenStream}
                      onLabelChange={(v) => onScreenSlotsChange(
                        screenShareSlots.map(s => s.id === curScreen.id ? { ...s, label: v } : s)
                      )}
                      onConfigChange={(c) => onScreenSlotsChange(
                        screenShareSlots.map(s => s.id === curScreen.id ? { ...s, config: c } : s)
                      )}
                      onResetStream={() => onScreenReset(curScreen.id)}
                    />
                  )}
                </div>
                <div style={{
                  flex: 1, minWidth: 0,
                  padding: 14,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(0,0,0,0.15)',
                }}>
                  {curScreenActive ? (
                    <DeviceStreamPreview stream={curScreenStream} />
                  ) : (
                    <div style={{ fontSize: 13, color: TXT.help, fontStyle: 'italic' }}>
                      Aucun flux d'écran
                    </div>
                  )}
                </div>
              </>
            );
          })()}

          <div style={{ flex: 1, overflow: 'hidden', display: tab === 'layouts' ? 'flex' : 'none' }}>
            <SceneConfigPanel
              layoutSlots={layoutSlots}
              availableSources={availableSources}
              onLayoutChange={onLayoutChange}
              initialLayoutIdx={initialLayoutIdx}
            />
          </div>

          <div style={{ flex: 1, overflow: 'hidden', display: tab === 'sounds' ? 'flex' : 'none', flexDirection: 'column' }}>
            {renderSounds()}
          </div>

          {tab === 'images' && (
            <>
              <div style={{
                width: 350, flexShrink: 0,
                borderRight: '1px solid rgba(255,255,255,0.06)',
                padding: '14px 14px',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
              }}>
                {renderImageLeft()}
              </div>
              <div style={{
                flex: 1, minWidth: 0,
                padding: '10px',
                display: 'flex', flexDirection: 'column', gap: 8,
                overflowY: 'auto', overflowX: 'hidden', scrollbarGutter: 'stable',
                background: 'rgba(0,0,0,0.15)',
              }}>
                <CanvasPreviewInteractive
                  items={imageCanvasItems}
                  onItemChange={handleImageItemChange}
                  highlightId={highlightId}
                  setHighlight={setHighlight}
                  lockedRatios={imageLockedRatios}
                  ratios={imageRatios}
                />
                <HintBar>
                  Cliquez un cadre pour le sélectionner · Glissez pour déplacer · Coins pour redimensionner<br />
                  🔗 = ratio verrouillé · Référence : {REF_W}×{REF_H} px
                </HintBar>
              </div>
            </>
          )}

          <div style={{ display: tab === 'jingles' ? 'contents' : 'none' }}>
            <div style={{
              width: 350, flexShrink: 0,
              borderRight: '1px solid rgba(255,255,255,0.06)',
              padding: '14px 14px',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}>
              {renderJingleLeft()}
            </div>
            <div style={{
              flex: 1, minWidth: 0,
              padding: '14px',
              display: 'flex', flexDirection: 'column', gap: 8,
              overflowY: 'auto', overflowX: 'hidden', scrollbarGutter: 'stable',
              background: 'rgba(0,0,0,0.15)',
            }}>
              <CanvasPreviewInteractive
                items={jingleCanvasItems}
                onItemChange={handleJingleItemChange}
                highlightId={highlightId}
                setHighlight={setHighlight}
                lockedRatios={jingleLockedRatios}
                ratios={jingleRatios}
              />
              <HintBar>
                Cliquez un cadre pour le sélectionner · Glissez pour déplacer · Coins pour redimensionner
              </HintBar>
            </div>
          </div>

        </div>

        {/* Pied — Annuler / Valider */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10,
          padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
        }}>
          <span style={{ marginRight: 'auto', fontSize: 12, color: TXT.faint }}>
            Les changements s'appliquent en direct · « Annuler » revient à l'état d'ouverture
          </span>
          <button
            type="button" onClick={handleCancel}
            style={{ ...HEAD_BTN, padding: '0 18px', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.05)', color: TXT.body }}
          >
            Annuler
          </button>
          <button
            type="button" onClick={handleValidate}
            style={{ ...HEAD_BTN, padding: '0 22px', cursor: 'pointer', border: '1px solid #818cf8', background: '#6366f1', color: '#ffffff', boxShadow: '0 4px 14px rgba(99,102,241,0.35)' }}
          >
            Valider
          </button>
        </div>
      </div>

      {mediaConfigDialog === 'save' && (
        <RegieConfigModal
          mode="save" kind="media"
          channelName={channelName} channelUuid={channelUuid} userName={userName}
          imageSlots={imageSlots} soundSlots={soundSlots} jingleSlots={jingleSlots} layoutSlots={layoutSlots}
          pipSlots={videoOverlaySlots}
          onClose={() => setMediaConfigDialog(null)}
        />
      )}
      {mediaConfigDialog === 'load' && (
        <RegieConfigModal
          mode="load" kind="media" channelUuid={channelUuid}
          onLoad={handleLoadMedia} onClose={() => setMediaConfigDialog(null)}
        />
      )}
    </div>
  );
};